/* eslint-disable quotes */
'use client';
import { createContext, useContext, useState, useEffect } from 'react';

// Create language context
const LanguageContext = createContext();

// Available languages
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇦🇪', dir: 'rtl' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
];

// Translations
export const translations = {
  en: {
    // Navigation
    aboutUs: 'About Us',
    properties: 'Properties',
    projects: 'Projects',
    buy: 'Buy',
    rent: 'Rent',
    offPlanResale: 'Off-Plan Resale',
    offPlan: 'Off-Plan',
    commercial: 'Commercial',
    properties: 'Properties',
    newsArticles: 'News / Articles',
    blog: 'Blog',
    agent: 'Agent',
    contactUs: 'Contact Us',
    services: 'Services',

    goldenVisa: 'Golden Visa',
    sellProperty: 'Sell Property',
    mortgage: 'Mortgage',

    // Hero Section
    heroTitle: 'Find Your Dream Property in Dubai',
    heroSubtitle:
      "Discover luxury properties in Dubai's most prestigious locations",

    // Property Search
    searchPlaceholder: 'City, community or building',
    propertyType: 'Property type',
    bedsAndBaths: 'Beds & Baths',
    search: 'Search',
    noResultsFound: 'No results found',
    tryDifferentSearch: 'Try a different search term or browse our properties',
    clearSearch: 'Clear search',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    reset: 'Reset',
    ok: 'OK',
    select: 'Select',
    beds: 'Beds',
    baths: 'Baths',
    commercial: 'Commercial',

    // Categories
    exploreServices: 'Explore Our Services',
    buyProperties: 'Buy Properties',
    buyDescription:
      'Find your perfect home from our curated selection of properties for sale.',
    rentProperties: 'Rent Properties',
    rentDescription:
      "Explore premium rental properties in Dubai's most sought-after locations.",
    offPlanProjects: 'Off-Plan Projects',
    offPlanDescription:
      "Invest in Dubai's future with our exclusive off-plan developments.",
    explore: 'Explore',

    // Featured Properties
    featuredProperties: 'Featured Properties',
    featuredDescription:
      "Discover our handpicked selection of Dubai's most exclusive properties",
    viewDetails: 'View Details',
    viewAllProperties: 'View All Properties',

    // Agents Section
    meetOurAgents: 'Meet Our Expert Agents',
    realEstateAgents: 'Real Estate Agents',
    agentsExperience:
      "Our professional agents have years of experience in the Dubai real estate market and are ready to help you find your dream property. With deep local knowledge and expertise in various property segments, they'll guide you through every step of your real estate journey.",
    buySellInvest:
      "Whether you're looking to buy, sell, or invest in Dubai's dynamic property market, our agents are committed to providing personalized service tailored to your specific needs and preferences",
    yearsExperience: 'Years Experience',
    propertiesSold: 'Properties Sold',
    contactAgent: 'Contact Agent',
    viewProfile: 'View Profile',
    meetAllAgents: 'Meet All Agents',
    showAllAgents: 'Show All',
    specialization: 'Specialization',
    languages: 'Languages',
    contactNow: 'Contact Now',

    // Top Sales Section
    topSalesAgents: 'Top Sales Agents',
    topSalesDescription: 'Recognizing our highest performing agents',
    filterByPeriod: 'Filter by Period',
    month: 'Month',
    quarter: 'Quarter',
    year: 'Year',
    salesVolume: 'Sales Volume',
    transactions: 'Transactions',
    ranking: 'Ranking',

    // Awards & Recognition
    awardsRecognition: 'Our Awards & Recognition',
    awardsDescription:
      "We're proud to be recognized for our excellence in real estate services and customer satisfaction.",
    scrollToExplore: 'Swipe to explore all awards',
    realEstateExcellence: 'Excellence in Real Estate',

    // Partner Developers
    partnerDevelopers: 'Our Partner Developers',
    partnerDevelopersDescription:
      "We work with Dubai's most prestigious developers to bring you exceptional properties and investment opportunities.",
    visitWebsite: 'Visit Website',

    // Why Choose Us
    whyChooseUs: 'Why Choose X Space',
    whyChooseUsDescription:
      "We're committed to providing exceptional service and tailored solutions for all your real estate needs in Dubai.",
    marketExpertise: 'Market Expertise',
    marketExpertiseDescription:
      'Our team has in-depth knowledge of the Dubai real estate market, ensuring you make informed investment decisions.',
    trustedAdvisors: 'Trusted Advisors',
    trustedAdvisorsDescription:
      "With thousands of successful transactions, we've built a reputation for honesty, integrity, and customer satisfaction.",
    timeEfficiency: 'Time Efficiency',
    timeEfficiencyDescription:
      'We value your time and streamline the property search process to find your ideal property quickly.',
    awardWinningService: 'Award-Winning Service',
    awardWinningServiceDescription:
      'Recognized for excellence in real estate services, we consistently exceed client expectations.',

    // News & Insights
    newsInsights: 'News & Insights',
    newsInsightsDescription:
      'Stay updated with the latest news, market trends, and insights from the Dubai real estate market.',
    latestNews: 'Latest News',
    readMore: 'Read More',
    featuredBlogPosts: 'Featured Blog Posts',
    viewAll: 'View All',
    browseAllArticles: 'Browse All Articles',

    // FAQ
    faq: 'Frequently Asked Questions',
    faqDescription:
      'Find answers to common questions about buying, selling, and investing in Dubai real estate.',
    cantFindAnswer: "Can't find the answer you're looking for?",
    contactOurTeam: 'Contact Our Team',

    // Call to Action
    readyToFindProperty: 'Ready to Find Your Dream Property?',
    readyToFindPropertyDescription:
      'Contact our expert team today and let us help you find the perfect Dubai property that matches your requirements and budget.',
    contactUsNow: 'Contact Us Now',
    viewMoreProperties: 'View More Properties',

    // Testimonials
    clientTestimonials: 'What Our Clients Say',
    clientTestimonialsDescription:
      'Read what our satisfied clients have to say about their experience working with X Space Real Estate.',

    // Time periods
    january: 'January',
    february: 'February',
    march: 'March',
    april: 'April',
    may: 'May',
    june: 'June',
    july: 'July',
    august: 'August',
    september: 'September',
    october: 'October',
    november: 'November',
    december: 'December',
    q1: 'Q1',
    q2: 'Q2',
    q3: 'Q3',
    q4: 'Q4',

    // Footer
    footerDescription:
      'Your trusted partner in Dubai real estate, offering exceptional properties and personalized service.',
    company: 'Company',
    properties: 'Properties',
    contactInfo: 'Contact Info',
    allRightsReserved: 'All rights reserved.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    cookiesPolicy: 'Cookies Policy',
    careers: 'Careers',
    newProjects: 'New Projects',
    workingHours: 'Mon - Sat, 9:30 - 6:30',
    location: 'Dubai, UAE',

    //About Us
    // atXSpace_1:
    //   'At X-Space Real Estate Broker, we are committed to making space accessible and profitable for our clients. Join us as we expand the boundaries of real estate and turn the dream of space colonization into a reality.',
    // atXSpace_2:
    //   'Explore the cosmos with us and secure your place in the future of humanity.',
    // welcometoXSpace:
    //   'Welcome to X-Space Real Estate Broker, your premier destination for buying, selling, and investing in the final frontier. As a pioneering space real estate brokerage, we specialize in connecting visionaries with prime extraterrestrial properties.',
    // lookingToAcquire:
    //   "Whether you're looking to acquire land on the Moon, develop commercial ventures on Mars, or invest in orbital habitats, our expert team is here to guide you through every step of the process.",
    // company_detail_1:
    //   'formerly known as Good Reputation Real Estate Broker is a leading real estate brokerage and property management company headquartered in Dubai. Since our inception in 2017, we have built a strong reputation for providing excellent service and exceptional customer experiences. Our dedicated team of real estate experts is committed to finding you the perfect property to meet your needs.',
    // company_detail_2:
    //   'Annually, our team successfully executes real estate transactions totaling more than AED 10 billion. By forging close partnerships with developers in the U.A.E, we ensure that our clients have access to the best options and services customized to their specific requirements. Our approach is centered around market knowledge, thorough research, and a commitment to providing tailored advice to our clients.',
    // company_detail_3:
    //   'At X-Space Real Estate, we uphold the values of honesty, trustworthiness, enthusiasm, thoughtfulness, sincerity, and responsibility. We operate in strict compliance with U.A.E laws and regulations to ensure the highest standards of professionalism and integrity.',

    atXSpace_1: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        At{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-Space Real Estate,
        </span>{' '}
        we are a trusted real estate agency in Dubai committed to making
        property accessible and profitable for our clients. We expand the
        boundaries of real estate excellence, turning the dream of finding your
        perfect home or investment into a reality.
      </p>
    ),
    atXSpace_2: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        As Dubai property experts, we are here to help you secure your place in
        the future of property.
      </p>
    ),

    learnMore: 'Learn More',
    yearsExperience: 'Years Experience',
    companyName: 'X-SPACE REAL ESTATE',
    welcometoXSpace: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        Welcome to{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-SPACE Real Estate,
        </span>{' '}
        your premier destination for buying, selling, and investing in the final
        frontier. We offer professional real estate services and specialize in
        connecting clients with exceptional opportunities in the market.
      </p>
    ),
    lookingToAcquire: (
      <p className=' text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'>
        Whether you're looking to{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          buy, sell, or rent property in Dubai,{' '}
        </span>
        develop a new venture, or invest in a unique asset, our expert team is
        here to guide you through every step
      </p>
    ),
    company_detail_1: (
      <p className=' text-justify'>
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-SPACE REAL ESTATE
        </span>{' '}
        formerly known as Good Reputation Real Estate Broker, is a leading real
        estate brokerage and property management company based in Dubai.
        Established in 2017, we have built a strong reputation for providing
        excellent service and exceptional customer experiences. Our dedicated
        team of multilingual real estate agents in Dubai is committed to finding
        you the perfect property to meet your needs.
      </p>
    ),
    company_detail_2: (
      <p className=' text-justify'>
        Each year, we handle over AED 10 billion in real estate transactions. We
        are your trusted property partner, building real estate relationships by
        partnering with top UAE developers to ensure our clients get the best
        properties and services. Our approach to smart property investment is
        centered around deep market knowledge, meticulous research, and a
        commitment to providing personalized advice.
      </p>
    ),
    company_detail_3: (
      <p className=' text-justify'>
        At X-SPACE , our core values of integrity, excellence, innovation, and
        respect guide everything we do. We operate in strict compliance with UAE
        laws and regulations to ensure the highest standards of professionalism
        and integrity in every transaction.
      </p>
    ),
    our_visionary_leaders: 'Our Visionary Leaders',
    our_visionary_description: (
      <div>
        <p className='text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto'>
          Meet the founders who are revolutionizing the real estate industry
          with their innovative approach and unwavering commitment to
          excellence.
        </p>
      </div>
    ),

    scroll_to_explore_agents: 'Swipe to explore all agents',

    our_values: 'Our Values',
    our_value_1: (
      <p className='text-gray-700 dark:text-gray-300 text-lg max-w-5xl mx-auto'>
        Our core values guide everything we do, shaping our company culture and
        influencing how we serve our clients in the Dubai real estate market .
        We operate with a diverse real estate team in Dubai , ensuring we
        provide top-tier service.
      </p>
    ),
    integrity: 'Integrity',
    integrity_description: (
      <div>
        We build trust through honesty and transparent business practices.
      </div>
    ),
    excellence: 'Excellence',
    excellence_description: (
      <div>
        We strive for real estate service excellence, exceeding expectations
        with every property we handle.
      </div>
    ),
    innovation: 'Innovation',
    innovation_description: (
      <div>
        We provide cutting-edge solutions through creative thinking and
        tech-enabled property services.
      </div>
    ),
    respect: 'Respect',
    respect_description: (
      <div>
        We value diverse perspectives and treat everyone with the utmost respect
        and dignity.
      </div>
    ),
    collaboration: 'Collaboration',
    collaboration_description: (
      <div>
        We believe in working together to achieve exceptional results for both
        our clients and our team.
      </div>
    ),
    responsibility: 'Responsibility',
    responsibility_description: (
      <div>
        We are committed to ethical practices and take responsibility for our
        actions, demonstrating our deep real estate market expertise in Dubai.
      </div>
    ),
    our_dynamic_team: 'Our Dynamic Team',
    our_dynamic_description: (
      <div>
        <p className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
          Meet the dedicated professionals who make X-Space Real Estate a leader
          in the industry.
        </p>
      </div>
    ),
    values: 'Values',
    values_description_1: (
      <p>
        At the heart of our operations, we prioritize clear communication,
        dedication, and exceptional customer service. Through attentive
        listening and a deep understanding of our clients' needs, we provide
        thorough and integrated real estate guidance. We offer innovative real
        estate solutions and take pride in our commitment to delivering
        outstanding outcomes that enhance the value of your property
        investments, in Dubai and internationally.
      </p>
    ),
    values_description_2: (
      <p>
        In every interaction, we strive for excellence in Dubai real estate and
        build lasting client relationships founded on trust and mutual respect.
        This ensures that every client interaction is characterized by
        professionalism and integrity, providing you with tailored real estate
        advice every step of the way.
      </p>
    ),
    vision_and_mission: 'Vision & Mission',
    vision: 'Our Vision',
    vision_description: (
      <p>
        Our goal is to become the leading real estate service provider in the
        region and the top choice for real estate professionals. We're dedicated
        to building lasting relationships with clients grounded in transparency
        in real estate and mutual trust, upholding the highest standards of{' '}
        professionalism.
      </p>
    ),
    mission: 'Our Mission',
    mission_description: (
      <p>
        We achieve our vision by fostering a dynamic, optimistic, and
        high-performance workplace that prioritizes the nurturing of enduring
        partnerships. Our success is measured by the exceptional results we
        deliver to our clients. We combine a driven mindset with a focus on
        personalized attention and outstanding customer service, guiding you
        through the process of property buying and selling and helping you
        secure a rewarding real estate investment.
      </p>
    ),
    visit_our_office: 'Visit Our Office',
    visit_our_office_description: (
      <div className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
        Conveniently located in Dubai, we invite you to visit us and discuss
        your real estate needs in person. Our office is where your Dubai real
        estate journey begins.
      </div>
    ),

    ready_dream_property: (
      <h2 className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'>
        Ready to Find Your{' '}
        <span className='text-[#D4AF37]'>Dream Property</span>?
      </h2>
    ),
    ready_dream_property_description: (
      <p className='text-lg text-gray-700 dark:text-gray-300 mb-8'>
        Let our expert team guide you. Whether you're buying property in Dubai,
        selling, or investing, we are here to help you every step of the way
        with our comprehensive real estate services.
      </p>
    ),
    personalized_service: 'Personalized Service',
    personalized_service_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        Tailored solutions for your unique requirements
      </p>
    ),
    market_expertise: 'Market Expertise',
    market_expertise_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        In-depth knowledge of Dubai's real estate market
      </p>
    ),
    exclusive_listings: 'Exclusive Listings',
    exclusive_listings_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        Access to premium properties not available elsewhere
      </p>
    ),

    // Services Page
    our_services: 'Our Services',
    our_services_description:
      'With over 9+ years of experience, X-Space Real Estate is a trusted real estate brokerage in Dubai providing complete property solutions for buyers, sellers, landlords, and investors. We specialize in buying, selling, and renting residential and commercial properties across Dubai’s prime and freehold communities. Our services include pre-sales and after-sales support, off-plan property investment guidance, mortgage consultancy, property valuation, and professional marketing to ensure maximum exposure and the best market value. We also provide Golden Visa assistance and detailed investment analysis to help clients make informed and profitable real estate decisions. Our experienced team is dedicated to delivering personalized service, market insights, and end-to-end support, helping you achieve your real estate goals with confidence in Dubai’s dynamic property market.',
    why_list_with_xspace: 'Why List with X-Space Real Estate',
    why_list_with_xspace_description:
      "X-Space Real Estate is your trusted partner in Dubai's property market. We are a top choice for property owners and investors, turning properties into profitable investments. Our seamless service and deep market knowledge make us the ideal partner for all your real estate needs in Dubai and the surrounding areas.",
    extensive_network: 'Extensive Network',
    extensive_network_description:
      'We leverage our broad network of professional agents and marketing partners to give your property maximum exposure.',
    legal_expertise: 'Legal Expertise',
    legal_expertise_description:
      'Our team provides comprehensive legal guidance, ensuring a smooth and seamless process for all Dubai property transactions.',
    media_resources: 'Media Resources',
    media_resources_description:
      "We utilize extensive media channels to maximize your property's exposure and Real Estate Services.",
    real_estate_services: 'Real Estate Services',
    service_buy: 'BUY',
    service_buy_title: 'Find Your Ideal Property in Dubai',
    service_buy_description:
      ' Whether you are looking to buy property for your dream house or a profitable investment, we will guide you. Our experts give you access to a wide range of properties for sale in Dubai, including luxury properties, villas, and apartments, ensuring you find the perfect match for your needs.',

    service_sell: 'SELL',
    service_sell_title: "Maximize Your Property's Exposure",
    service_sell_description:
      'Our team provides comprehensive selling services to get the best return on your investment. We use our broad network and marketing resources to give your property management and listings maximum exposure.',

    service_rent: 'RENT',
    service_rent_title: 'Your Trusted Partner for Rentals',
    service_rent_description:
      'Our real estate experts will guide you through the process of finding your ideal rental. We provide access to an extensive database of properties for rent in Dubai, and offer services like short-term rentals, tenant screening, and propertyinspection.',

    residency_solutions: 'Residency Solutions',
    residency_solutions_title_1: 'Golden Visa Services',
    residency_solutions_title_2: 'Your Path to Residency in the UAE',
    residency_solutions_description:
      'We guide clients through the process of securing a Dubai Golden Visa. Our team guarantees a smooth application by providing the right property investment options and documentation support, making residency in Dubai easier and more accessible. We handle all visa services related to your real estate purchase.',

    property_investment_guidance: 'Property Investment Guidance',
    documentation_support: 'Documentation Support',
    application_processing: 'Application Processing',
    legal_compliance: 'Legal Compliance',

    financing_solutions: 'Financing Solutions',
    financing_solutions_title_1: 'Mortgage Consultancy',
    financing_solutions_title_2: 'Find the Right Financing for Your Property',
    financing_solutions_description:
      'Our mortgage experts simplify the home financing process. We offer personalized consultations to help you find the best loan and financing options available, making it easy to secure your dream property in Dubai.',

    personalized_consultations: 'Personalized Consultations',
    best_loan_options: 'Best Loan Options',
    financing_solutions: 'Financing Solutions',
    application_support: 'Application Support',

    ready_to_experience: (
      <h2 className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'>
        Ready to Experience{' '}
        <span className='text-[#D4AF37]'> Premium Real Estate Services</span> ?
      </h2>
    ),
    ready_to_experience_description:
      'As your trusted partner for all your real estate needs, we offer customized and comprehensive services to meet your unique requirements. Choose X-Space for expert support and seamless real estate solutions.',
    contact_us_today: 'Contact Us Today',

    // Services Page Golden Visa
    uae_residency_solutions: 'UAE Residency Solutions',
    golden_visa_services: 'Golden Visa Services',
    your_path: 'Your Path to Residency in the UAE',
    your_path_description:
      'We guide clients through the process of securing a Dubai Golden Visa. Our team guarantees a smooth application by providing the right UAE property investment options and documentation support, making Dubai residency easier and more accessible.',
    start_application: 'Start Your Application',
    uae_golden_visa_benefits: 'UAE Golden Visa Benefits',
    uae_golden_visa_benefits_description:
      'The UAE Golden Visa offers exceptional benefits for investors, entrepreneurs, and skilled professionals who want to make the UAE their long-term home.',
    ten_year_residency: '10-Year Residency',
    ten_year_residency_description:
      'Secure a renewable long-term residency visa valid for a decade, providing stability and security.',
    family_inclusion: 'Family Inclusion',
    family_inclusion_description:
      'Extend your UAE residency to your spouse, children, and parents, allowing your family to live with you in the country.',
    no_sponsor_required: 'No Sponsor Required',
    no_sponsor_required_description:
      'Enjoy complete independence. The Golden Visa grants you residency without a UAE sponsor, giving you greater freedom.',
    multiple_entry: 'Multiple Entry',
    multiple_entry_description:
      'Experience unrestricted travel with unlimited entry and exit from the UAE, ideal for business and personal trips.',
    property_ownership: 'Property Ownership',
    property_ownership_description:
      'Gain full property ownership in designated freehold areas, a key benefit for real estate investors in Dubai and the UAE.',
    business_opportunities: 'Business Opportunities',
    business_opportunities_description:
      'Unlock the potential to establish and own a business with 100% ownership in many thriving sectors across the UAE.',
    golden_visa_eligibility_criteria: 'Golden Visa Eligibility Criteria',
    property_investment_route: 'Property Investment Route',
    property_investment_route_description:
      'Discover the eligibility criteria to secure your UAE Golden Visa through strategic investment or specialized talent.',
    min_investment: 'Minimum Investment: AED 2 Million',
    min_investment_description:
      'Qualify for the Golden Visa by purchasing property in Dubai or other designated emirates valued at a minimum of AED 2 million.',
    freehold_properties: 'Freehold Properties',
    freehold_properties_description:
      'Investment must be in UAE freehold properties, granting you full ownership rights in approved areas.',
    no_loan_restrictions: 'No Loan Restrictions',
    no_loan_restrictions_description:
      'The property can be purchased with a mortgage, offering flexible options for your investment',
    business_investment_route: 'Business Investment Route',
    business_investment_route_description:
      'Qualify through business investment and entrepreneurial activities in the UAE.',
    business_investment_aed_10_million: 'Business Investment: AED 10 Million',
    business_investment_aed_10_million_description:
      'Secure your visa by establishing or investing in UAE businesses with a minimum capital of AED 10 million.',
    specialized_talents: 'Specialized Talents',
    specialized_talents_description:
      'The Golden Visa is available for highly skilled professionals, including doctors, engineers, scientists, and artists, who can demonstrate exceptional contributions.',
    outstanding_students: 'Outstanding Students',
    outstanding_students_description:
      'High-achieving students and graduates are also eligible, recognizing academic excellence as a path to long-term residency.',
    our_golden_visa_process: 'Our Golden Visa Process',
    initial_consultation: 'Initial Consultation',
    initial_consultation_description:
      'We begin with a complimentary consultation to assess your eligibility and guide you toward the best Dubai Golden Visa application route for your specific needs.',
    property_selection: 'Property Selection',
    property_selection_description:
      'Our experts provide tailored guidance to help you select the ideal property investment in Dubai that fully meets the Golden Visa requirements.',
    property_documentation: 'Documentation',
    property_documentation_description:
      'We offer comprehensive assistance with all necessary document preparation, attestation, and submission to the relevant government authorities. We ensure every detail is handled correctly.',
    visa_approval: 'Visa Approval',
    visa_approval_description:
      'Follow-up on application status and support through the final approval and visa issuance process.',
    expert_guidance: 'Expert Guidance',
    expert_guidance_description: 'Why Choose X-Space for Your Golden Visa?',
    expert_guidance_description_1:
      "With over 9 years of experience in Dubai's real estate market and in-depth knowledge of UAE Golden Visa regulations, we provide end-to-end support for your residency journey.",
    expert_guidance_description_2:
      'Expert Knowledge of UAE Visa Regulations: We specialize in the latest Dubai Golden Visa requirements, ensuring your application is compliant and successful.',
    expert_guidance_description_3:
      'Comprehensive Property Investment Guidance: Our experts help you find the best real estate for Golden Visa eligibility, maximizing your investment potential.',
    expert_guidance_description_4:
      'Complete Documentation Support: We handle all necessary paperwork, ensuring a smooth and accurate submission for your Golden Visa application.',
    expert_guidance_description_5:
      'Dedicated Visa Application Management: We manage your entire Dubai residency visa process, from initial consultation to final approval.',
    expert_guidance_description_6:
      'Post-Approval Services: Our support extends beyond visa approval, offering help with settlement and integration into the UAE.',
    expert_guidance_description_7:
      'Family Visa Processing Assistance: We provide full support to help your spouse, children, and parents also secure their UAE family visa.',
    expert_guidance_description_8:
      'Legal Compliance & Advisory: Our team offers expert legal advice, ensuring your Golden Visa investment is fully compliant with UAE law.',
    expert_guidance_description_9:
      'Ongoing Support & Consultation: We are always available for ongoing Golden Visa consultation and support, offering peace of mind throughout your journey.',
    ready_to_secure: 'Ready to Secure Your UAE Golden Visa?',
    ready_to_secure_description:
      'Take the first step towards UAE residency. Our experts are ready to guide you through the entire Golden Visa process and help you find the perfect property investment.',
    golden_visa_consultation: 'Golden Visa Consultation',

    // Services Page Sell Your Property
    property_selling: 'Property Selling Services',
    sell_your_property: 'Sell Your Property',
    maximize_your_property: "Maximize Your Property's Exposure in Dubai",
    maximize_your_property_description:
      'Our team provides comprehensive property selling services to get the best return on your investment. We use our broad network and powerful marketing resources to give your real estate listings maximum exposure, ensuring your property management and sale process is seamless and successful.',
    get_free_valuation: 'Get Free Property Valuation',
    why_choose_x_space: 'Why Choose X-Space to Sell Your Property?',
    why_choose_x_space_description:
      "With over 9 years of expertise in Dubai's real estate market, we provide unmatched property selling services that maximize your home’s value and minimize its time on the market.",
    maximize_exposure: 'Maximum Exposure',
    maximize_exposure_description:
      'We leverage our extensive network and powerful marketing channels for optimal property listings visibility, ensuring your home reaches a wide audience of qualified buyers.',
    expert_valuation: 'Expert Valuation',
    expert_valuation_description:
      'Get accurate and reliable property valuations based on current market trends and comparable sales in your area, helping you price your property right.',
    professional_marketing: 'Professional Marketing',
    professional_marketing_description:
      'Our services include high-quality photography, immersive virtual tours, and premium listing presentations to make your property for sale stand out.',
    dedicated_support: 'Dedicated Support',
    dedicated_support_description:
      "You'll have a personal agent providing dedicated support throughout the entire real estate selling process, from the initial listing to the final closing.",
    our_proven_selling_process: 'Our Proven Selling Process',
    property_evaluation: 'Property Evaluation',
    property_evaluation_description:
      'We begin with a comprehensive real estate market analysis and an accurate property valuation to determine the optimal pricing strategy for your listing.',
    marketing_strategy: 'Marketing Strategy',
    marketing_strategy_description:
      'Our strategy includes professional photography, immersive virtual tours, and a multi-channel marketing campaign to give your property for sale maximum visibility.',
    buyer_screening: 'Buyer Screening',
    buyer_screening_description:
      'We handle the pre-screening of all potential buyers, identifying qualified individuals to ensure a seamless and serious purchase process.',
    negotiation: 'Negotiation',
    negotiation_description:
      'Our experienced agents provide expert negotiation to secure the best possible price and favorable terms for your property sale.',
    documentation: 'Documentation',
    documentation_description:
      'We manage all legal documentation, ensuring full compliance with Dubai property regulations for a hassle-free transaction.',
    closing: 'Closing',
    closing_description:
      'Our team provides full support through the final transfer process, guaranteeing a smooth and successful closing for your Dubai property sale',
    comprehensive_services: 'Comprehensive Services',
    whats_included: "What's Included in Our Service",
    whats_included_description:
      'Our comprehensive service includes everything you need to successfully sell your property in Dubai at the best possible price in the shortest time frame.',
    whats_included_description_1:
      'Free Professional Property Valuation: Get a complimentary and accurate real estate valuation to determine the optimal listing price for your property.',
    whats_included_description_2:
      'High-Quality Photography & Virtual Tours: We create premium visuals, including high-quality photos and immersive virtual tours, to make your property listing stand out.',
    whats_included_description_3:
      'Multi-Platform Marketing Campaign: Your property will be promoted through a multi-channel campaign to gain maximum exposure and attract qualified buyers.',
    whats_included_description_4:
      'Qualified Buyer Screening & Showings: We handle all buyer inquiries and pre-screen potential clients to ensure only serious offers are considered.',
    whats_included_description_5:
      'Expert Negotiation & Price Optimization: Our experienced team expertly negotiates on your behalf to secure the best price and terms for your property sale.',
    whats_included_description_6:
      'Complete Legal Documentation Support: We manage all legal paperwork and ensure full compliance with Dubai real estate regulations for a seamless transaction.',
    whats_included_description_7:
      'Transaction Management & Closing Support: We provide end-to-end support through the final closing process to ensure a smooth and timely sale.',
    whats_included_description_8:
      "Post-Sale Support & Assistance: Our service doesn't end at closing; we offer continued support and assistance for any post-sale needs.",
    start_selling_process: 'Start Selling Process',
    ready_to_sell_your_property: 'Ready to Sell Your Property?',
    get_free_property_valuation: 'Get Free Property Valuation',
    ready_to_sell_your_property_description:
      "Get started with a free property valuation and consultation. Our experts are ready to help you maximize your property's value and ensure a smooth selling experience.",

    // Services Page Mortgage Consultancy
    financing_solutions: 'Financing Solutions',
    mortgage_consultancy: 'Mortgage Consultancy',
    find_the_right_financing: 'Find the Right Financing for Your Property',
    find_the_right_financing_description:
      'Our mortgage experts simplify the home financing process. We offer personalized consultations to help you find the best loan and financing options available, making it easy to secure your dream property in Dubai.',
    get_pre_approved: 'Get Pre-Approved',
    our_mortgage_services: 'Our Mortgage Services',
    our_mortgage_services_description:
      "Comprehensive mortgage solutions tailored to your financial needs and property investment goals in Dubai's dynamic real estate market.",
    mortgage_pre_approval: 'Mortgage Pre-Approval',
    mortgage_pre_approval_description:
      'Get pre-approved for your mortgage to strengthen your buying position and streamline the purchase process.',

    investment_calculator: 'Investment Calculator',
    investment_calculator_description:
      'Calculate your potential returns with our comprehensive real estate investment calculator.',
    dubai_real_estate_investments: 'Dubai Real Estate Investments Calculator',
    dubai_real_estate_investments_description:
      'Analyze, calculate and evaluate potential real estate returns',
    dubai_real_estate_investments_hold: 'Hold',
    dubai_real_estate_investments_flip: 'Flip',
    expected_capital_appreciation: 'Expected Capital Appreciation',
    expected_total_rental_income: 'Total Rental Income',
    expected_aed: 'AED',
    expected_usd: 'USD',
    expected_investment_amount: 'Enter investment amount',
    expected_annual_appreciation: 'Expected Annual Appreciation',
    expected_annual_rental_yield: 'Expected Annual Rental Yield',
    expected_holding_period: 'Holding Period (Years)',
    expected_value_after: 'Value After',
    expected_years: 'Years',
    expected_value_exit_fee: 'Exit Fee (2.5%)',
    expected_value_annual_cash_flow: 'Annual Cash Flow',
    expected_value_entry_fee: 'Entry Fee (1.5%)',
    expected_reset: 'Reset',
    expected_investment_duration: 'Investment Duration:',
    expected_expected_irr: 'Expected IRR',
    expected_exit_multiple: 'Exit Multiple',
    expected_project_duration: 'Project Duration (Years)',
    expected_total_roi: 'Total ROI',
    expected_flip_entry_fee: 'Entry Fee (2.5%)',
    expected_flip_exit_fee: 'Exit Fee (2.5%)',

    rate_comparison: 'Rate Comparison',
    rate_comparison_description:
      'Compare mortgage rates from multiple banks and financial institutions to secure the best deal.',
    loan_structuring: 'Loan Structuring',
    loan_structuring_description:
      'Expert advice on loan structuring, terms, and repayment options that suit your financial situation.',
    documentation_support: 'Documentation Support',
    documentation_support_description:
      'Complete assistance with mortgage documentation, application processing, and submission.',
    fast_processing: 'Fast Processing',
    fast_processing_description:
      'Expedited mortgage processing with our established banking relationships and streamlined procedures.',
    expert_guidance: 'Expert Guidance',
    expert_guidance_description:
      'Personalized consultation from certified mortgage specialists with deep market knowledge.',
    mortgage_types_available: 'Mortgage Types Available',
    conventional_mortgage: 'Conventional Mortgages',
    conventional_mortgage_description:
      'Traditional mortgage options with competitive rates and flexible terms for property purchases in Dubai.',
    fixed_rate_mortgage: 'Fixed Rate Mortgages',
    fixed_rate_mortgage_description:
      'Stable monthly payments with fixed interest rates',
    variable_rate_mortgage: 'Variable Rate Mortgages',
    variable_rate_mortgage_description:
      'Flexible rates that adjust with market conditions',
    up_to_80_financing: 'Up to 80% Financing',
    up_to_80_financing_description:
      'High loan-to-value ratios for qualified buyers',
    islamic_mortgage: 'Islamic Mortgages',
    islamic_mortgage_description:
      'Sharia-compliant financing solutions that adhere to Islamic banking principles and regulations.',
    murabaha_financing: 'Murabaha Financing',
    murabaha_financing_description:
      'Cost-plus financing structure compliant with Islamic law',
    ijara_financing: 'Ijara Financing',
    ijara_financing_description:
      'Lease-to-own arrangements with gradual ownership transfer',
    sharia_compliance: 'Sharia Compliance',
    sharia_compliance_description:
      'Fully compliant with Islamic banking principles',
    our_mortgage_process: 'Our Mortgage Process',
    initial_consultation: 'Initial Consultation',
    initial_consultation_description_mortgage:
      'Free consultation to assess your financial situation and determine the best mortgage options.',
    pre_approval: 'Pre-Approval',
    pre_approval_description:
      'Get pre-approved for your mortgage to understand your budget and strengthen your offer.',
    application_processing: 'Application Processing',
    application_processing_description:
      'Complete mortgage application with our support and submit to preferred lenders.',
    approval_closing: 'Approval & Closing',
    approval_closing_description:
      'Final approval, property valuation, and closing process with full support.',
    expert_mortgage_services: 'Expert Mortgage Services',
    expert_mortgage_services_description:
      'Why Choose Our Mortgage Consultancy?',
    expert_mortgage_services_description_1:
      "With extensive experience in Dubai's mortgage market and strong relationships with leading banks, we provide comprehensive mortgage solutions tailored to your specific needs.",
    expert_mortgage_services_description_2:
      'Access to 15+ Leading Banks & Lenders',
    expert_mortgage_services_description_3:
      'Competitive Interest Rates & Terms',
    expert_mortgage_services_description_4: 'Fast Pre-Approval Process',
    expert_mortgage_services_description_5:
      'Expert Financial Analysis & Advice',
    expert_mortgage_services_description_6: 'Complete Documentation Support',
    expert_mortgage_services_description_7: 'Personalized Mortgage Solutions',
    expert_mortgage_services_description_8: 'Post-Approval Support & Guidance',
    expert_mortgage_services_description_9: 'No Hidden Fees or Charges',
    ready_to_secure_mortgage: 'Ready to Secure Your Mortgage?',
    ready_to_secure_mortgage_description:
      'Get started with a free mortgage consultation and pre-approval. Our experts are ready to help you find the best financing solution for your property purchase. solution for your property purchase.',
    mortgage_consultation: 'Mortgage Consultation',
  },
  ar: {
    // Navigation
    aboutUs: 'من نحن',
    properties: 'العقارات',
    projects: 'المشاريع',
    buy: 'شراء',
    offPlanResale: 'إعادة بيع المشاريع على المخطط',
    offPlan: 'مشاريع على المخطط',
    commercial: 'تجاري',
    properties: 'العقارات',
    newsArticles: 'الأخبار / المقالات',
    newsArticles: 'الأخبار / المقالات',
    blog: 'المدونة',
    agent: 'الوكيل',
    contactUs: 'اتصل بنا',

    // Hero Section
    heroTitle: 'ابحث عن عقارك المثالي في دبي',
    heroSubtitle: 'اكتشف العقارات الفاخرة في أرقى مواقع دبي',

    // Property Search
    searchPlaceholder: 'مدينة، مجتمع أو مبنى',
    propertyType: 'نوع العقار',
    bedsAndBaths: 'الغرف والحمامات',
    search: 'بحث',
    noResultsFound: 'لم يتم العثور على نتائج',
    tryDifferentSearch: 'حاول البحث بمصطلح مختلف أو تصفح عقاراتنا',
    clearSearch: 'مسح البحث',

    // Categories
    exploreServices: 'استكشف خدماتنا',
    buyProperties: 'شراء العقارات',
    buyDescription:
      'اعثر على منزلك المثالي من بين مجموعة مختارة من العقارات المعروضة للبيع.',
    rentProperties: 'استئجار العقارات',
    rentDescription: 'استكشف العقارات الفاخرة للإيجار في أكثر مواقع دبي طلبًا.',
    offPlanProjects: 'مشاريع خارج الخطة',
    offPlanDescription: 'استثمر في مستقبل دبي مع مشاريعنا الحصرية خارج الخطة.',
    explore: 'استكشاف',

    // Featured Properties
    featuredProperties: 'العقارات المميزة',
    featuredDescription:
      'اكتشف مجموعة مختارة بعناية من أكثر العقارات الحصرية في دبي',
    viewDetails: 'عرض التفاصيل',
    viewAllProperties: 'عرض جميع العقارات',

    // Agents Section
    meetOurAgents: 'تعرف على وكلائنا الخبراء',
    realEstateAgents: 'وكلاء العقارات',
    agentsExperience:
      'يتمتع وكلاؤنا المحترفون بسنوات من الخبرة في سوق العقارات في دبي، وهم مستعدون لمساعدتك في العثور على عقارك المثالي. بفضل معرفتهم العميقة بالسوق المحلي وخبرتهم في مختلف القطاعات العقارية، سيرشدونك خلال كل خطوة في رحلتك العقارية.',
    buySellInvest:
      'سواء كنت تبحث عن شراء أو بيع أو الاستثمار في سوق العقارات الديناميكي في دبي، يلتزم وكلاؤنا بتقديم خدمة شخصية مصممة خصيصًا لتلبية احتياجاتك وتفضيلاتك الخاصة.',
    yearsExperience: 'سنوات الخبرة',
    propertiesSold: 'العقارات المباعة',
    contactAgent: 'اتصل بالوكيل',
    viewProfile: 'عرض الملف الشخصي',
    meetAllAgents: 'التعرف على جميع الوكلاء',
    showAllAgents: 'عرض الكل',
    specialization: 'التخصص',
    languages: 'اللغات',
    contactNow: 'اتصل الآن',

    // Top Sales Section
    topSalesAgents: 'أفضل وكلاء المبيعات',
    topSalesDescription: 'تكريم وكلائنا الأعلى أداءً',
    filterByPeriod: 'تصفية حسب الفترة',
    month: 'الشهر',
    quarter: 'الربع',
    year: 'السنة',
    salesVolume: 'حجم المبيعات',
    transactions: 'المعاملات',
    ranking: 'التصنيف',

    // Awards & Recognition
    awardsRecognition: 'جوائزنا وتقديراتنا',
    awardsDescription:
      'نفخر بالاعتراف بنا لتميزنا في خدمات العقارات ورضا العملاء.',
    scrollToExplore: 'اسحب لاستكشاف جميع الجوائز',
    realEstateExcellence: 'التميز في العقارات',

    // Partner Developers
    partnerDevelopers: 'المطورون الشركاء',
    partnerDevelopersDescription:
      'نعمل مع أرقى المطورين في دبي لنقدم لك عقارات وفرص استثمارية متميزة.',
    visitWebsite: 'زيارة الموقع',

    // Why Choose Us
    whyChooseUs: 'لماذا تختار X Space',
    whyChooseUsDescription:
      'نحن ملتزمون بتقديم خدمات استثنائية وحلول مخصصة لجميع احتياجاتك العقارية في دبي.',
    marketExpertise: 'الخبرة في السوق',
    marketExpertiseDescription:
      'فريقنا لديه معرفة متعمقة بسوق العقارات في دبي، مما يضمن لك اتخاذ قرارات استثمارية مستنيرة.',
    trustedAdvisors: 'مستشارون موثوقون',
    trustedAdvisorsDescription:
      'مع آلاف المعاملات الناجحة، بنينا سمعة قائمة على النزاهة والشفافية ورضا العملاء.',
    timeEfficiency: 'الكفاءة في الوقت',
    timeEfficiencyDescription:
      'نقدر وقتك ونقوم بتبسيط عملية البحث عن العقار لإيجاد العقار المثالي بسرعة.',
    awardWinningService: 'خدمة حائزة على جوائز',
    awardWinningServiceDescription:
      'تم الاعتراف بنا للتميز في خدمات العقارات، ونسعى دائمًا لتجاوز توقعات العملاء.',

    // News & Insights
    newsInsights: 'الأخبار والرؤى',
    newsInsightsDescription:
      'ابقَ على اطلاع بآخر الأخبار واتجاهات السوق والرؤى حول سوق العقارات في دبي.',
    latestNews: 'آخر الأخبار',
    readMore: 'اقرأ المزيد',
    featuredBlogPosts: 'المقالات المميزة',
    viewAll: 'عرض الكل',
    browseAllArticles: 'تصفح جميع المقالات',

    // FAQ
    faq: 'الأسئلة الشائعة',
    faqDescription:
      'ابحث عن إجابات للأسئلة الشائعة حول شراء وبيع والاستثمار في العقارات في دبي.',
    cantFindAnswer: 'لا يمكنك العثور على الإجابة التي تبحث عنها؟',
    contactOurTeam: 'اتصل بفريقنا',

    // Call to Action
    readyToFindProperty: 'هل أنت مستعد للعثور على عقارك المثالي؟',
    readyToFindPropertyDescription:
      'اتصل بفريقنا الخبير اليوم ودعنا نساعدك في العثور على العقار المثالي في دبي الذي يناسب متطلباتك وميزانيتك.',
    contactUsNow: 'اتصل بنا الآن',
    viewMoreProperties: 'عرض المزيد من العقارات',

    // Testimonials
    clientTestimonials: 'آراء عملائنا',
    clientTestimonialsDescription:
      'اقرأ ما يقوله عملاؤنا الراضون عن تجربتهم مع X Space Real Estate.',

    // Property Details
    bedrooms: 'غرف النوم',
    bathrooms: 'الحمامات',
    reset: 'إعادة تعيين',
    ok: 'موافق',
    select: 'اختيار',
    beds: 'غرف',
    baths: 'حمامات',
    commercial: 'تجاري',

    // Time periods
    january: 'يناير',
    february: 'فبراير',
    march: 'مارس',
    april: 'أبريل',
    may: 'مايو',
    june: 'يونيو',
    july: 'يوليو',
    august: 'أغسطس',
    september: 'سبتمبر',
    october: 'أكتوبر',
    november: 'نوفمبر',
    december: 'ديسمبر',
    q1: 'الربع الأول',
    q2: 'الربع الثاني',
    q3: 'الربع الثالث',
    q4: 'الربع الرابع',

    // Footer
    footerDescription:
      'شريكك الموثوق في عقارات دبي، حيث نقدم عقارات استثنائية وخدمة متميزة.',
    company: 'الشركة',
    properties: 'العقارات',
    contactInfo: 'معلومات الاتصال',
    allRightsReserved: 'جميع الحقوق محفوظة.',
    privacyPolicy: 'سياسة الخصوصية',
    termsOfService: 'شروط الخدمة',
    cookiesPolicy: 'سياسة ملفات تعريف الارتباط',
    careers: 'الوظائف',
    services: 'الخدمات',
    newProjects: 'المشاريع الجديدة',
    workingHours: 'الإثنين - السبت، 9:30 - 6:30',
    location: 'دبي، الإمارات العربية المتحدة',

    atXSpace_1: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        في{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-Space العقارية،
        </span>{' '}
        نحن وكالة عقارية موثوقة في دبي ملتزمون بجعل العقارات متاحة ومربحة
        لعملائنا. نحن نوسع حدود التميز في مجال العقارات، ونجعل حلمك بالعثور على
        المنزل المثالي أو الاستثمار حقيقة.
      </p>
    ),
    atXSpace_2: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'>
        بصفتنا خبراء عقارات دبي، نحن هنا لمساعدتك على تأمين مكانك في مستقبل
        العقارات.
      </p>
    ),

    learnMore: 'اعرف المزيد',
    yearsExperience: 'سنوات الخبرة',
    companyName: 'إكس سبيس العقارية',
    welcometoXSpace: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        مرحبًا بكم في{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          شركة إكس سبيس العقارية،
        </span>{' '}
        وجهتكم الأولى للشراء والبيع والاستثمار في آفاق جديدة. نقدم خدمات عقارية
        مهنية ونتخصص في ربط العملاء بفرص استثنائية في السوق.
      </p>
    ),
    lookingToAcquire: (
      <p className=' text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'>
        سواء كنت تبحث عن{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          شراء أو بيع أو استئجار عقار في دبي،{' '}
        </span>
        أو تطوير مشروع جديد، أو الاستثمار في أصل فريد، فإن فريقنا الخبير هنا
        لإرشادك في كل خطوة.
      </p>
    ),
    company_detail_1: (
      <p className=' text-justify'>
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-SPACE العقارية
        </span>{' '}
        المعروفة سابقًا باسم "سمعة طيبة للعقارات"، هي شركة رائدة في الوساطة
        العقارية وإدارة الممتلكات مقرها دبي. تأسست في عام 2017، وقد بنينا سمعة
        قوية في تقديم خدمات مميزة وتجارب عملاء استثنائية. فريقنا المكون من وكلاء
        عقاريين متعددي اللغات في دبي ملتزم بإيجاد العقار المثالي الذي يناسب
        احتياجاتك.
      </p>
    ),
    company_detail_2: (
      <p className=' text-justify'>
        كل عام، نتعامل مع أكثر من 10 مليارات درهم من معاملات العقارات. نحن شريكك
        العقاري الموثوق، نبني علاقات عقارية من خلال الشراكة مع كبار مطوري
        العقارات في الإمارات لضمان حصول عملائنا على أفضل العقارات والخدمات.
        نهجنا في الاستثمار العقاري الذكي يعتمد على معرفة عميقة بالسوق، وبحوث
        دقيقة، والتزام بتقديم نصائح شخصية.
      </p>
    ),
    company_detail_3: (
      <p className=' text-justify'>
        في X-SPACE ، قيمنا الأساسية المتمثلة في النزاهة والتميز والابتكار
        والاحترام توجه كل ما نقوم به. نحن نعمل وفقًا للقوانين واللوائح
        الإماراتية لضمان أعلى معايير الاحترافية والنزاهة في كل معاملة.
      </p>
    ),
    our_visionary_leaders: 'قادتنا أصحاب الرؤية',
    our_visionary_description: (
      <div>
        <p className='text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto'>
          تعرف على المؤسسين الذين يحدثون ثورة في قطاع العقارات من خلال نهجهم
          المبتكر والتزامهم الراسخ بالتميز.
        </p>
      </div>
    ),

    scroll_to_explore_agents: 'مرر لاستكشاف جميع الوكلاء',

    our_values: 'قيمنا',
    our_value_1: (
      <p className='text-gray-700 dark:text-gray-300 text-lg max-w-5xl mx-auto'>
        قيمنا الأساسية توجه كل ما نقوم به، وتشكل ثقافة شركتنا وتؤثر على كيفية
        خدمتنا لعملائنا في سوق العقارات في دبي . نحن نعمل مع فريق عقاري متنوع في
        دبي ، مما يضمن تقديمنا خدمة من أعلى مستوى.
      </p>
    ),
    integrity: 'النزاهة',
    integrity_description: (
      <div>نحن نبني الثقة من خلال الصدق والممارسات الشفافة.</div>
    ),
    excellence: 'التميز',
    excellence_description: (
      <div>
        نحن نسعى لتحقيق التميز في الخدمات العقارية، وتجاوز التوقعات مع كل عقار
        نتعامل معه.
      </div>
    ),
    innovation: 'الابتكار',
    innovation_description: (
      <div>
        نحن نقدم حلولًا متطورة من خلال التفكير الإبداعي و الخدمات العقارية
        المدعمة بالتكنولوجيا.
      </div>
    ),
    respect: 'الاحترام',
    respect_description: (
      <div>نحن نقدر وجهات النظر المتنوعة ونعامل الجميع باحترام وكرامة.</div>
    ),
    collaboration: 'التعاون',
    collaboration_description: (
      <div>نحن نؤمن بالعمل معًا لتحقيق نتائج استثنائية لعملائنا وفريقنا.</div>
    ),
    responsibility: 'المسؤولية',
    responsibility_description: (
      <div>
        نحن ملتزمون بالممارسات الأخلاقية ونتحمل مسؤولية أفعالنا، مما يعكس خبرتنا
        العميقة في سوق العقارات بدبي.
      </div>
    ),
    our_dynamic_team: 'فريقنا الديناميكي',
    our_dynamic_description: (
      <div>
        <p className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
          تعرف على المحترفين المكرسين الذين يجعلون X-Space رائدة في القطاع.
        </p>
      </div>
    ),
    values: 'القيم',
    values_description_1: (
      <p>
        في صميم عملياتنا، نعطي الأولوية للتواصل الواضح، والتفاني، وخدمة العملاء
        الاستثنائية. من خلال الاستماع بعناية وفهم عميق لاحتياجات عملائنا، نقدم
        إرشادات عقارية شاملة ومتكاملة. نحن نقدم حلولًا عقارية مبتكرة ونفخر
        بالتزامنا بتقديم نتائج متميزة تعزز قيمة استثماراتك العقارية، في دبي وعلى
        المستوى الدولي.
      </p>
    ),
    values_description_2: (
      <p>
        في كل تعامل، نسعى لتحقيق التميز في عقارات دبي وبناء علاقات طويلة الأمد
        مع العملاء قائمة على الثقة والاحترام المتبادل. هذا يضمن أن كل تعامل
        يتميز بالاحترافية والنزاهة، ويوفر لك نصائح عقارية مخصصة في كل خطوة.
      </p>
    ),
    vision_and_mission: 'الرؤية والرسالة',
    vision: 'رؤيتنا',
    vision_description: (
      <p>
        هدفنا أن نصبح المزود الرائد للخدمات العقارية في المنطقة والخيار الأول
        للمهنيين العقاريين. نحن ملتزمون ببناء علاقات طويلة الأمد مع العملاء
        مبنية على الشفافية في العقارات والثقة المتبادلة، مع الالتزام بأعلى
        معايير الاحترافية.
      </p>
    ),
    mission: 'رسالتنا',
    mission_description: (
      <p>
        نحن نحقق رؤيتنا من خلال تعزيز بيئة عمل ديناميكية ومتفائلة عالية الأداء
        تركز على بناء شراكات دائمة. يتم قياس نجاحنا بالنتائج الاستثنائية التي
        نقدمها لعملائنا. نحن نمزج بين عقلية دافعة واهتمام شخصي وخدمة عملاء
        متميزة، لإرشادك في عملية شراء وبيع العقارات ومساعدتك على تأمين استثمار
        عقاري مربح.
      </p>
    ),
    visit_our_office: 'قم بزيارة مكتبنا',
    visit_our_office_description: (
      <div className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
        يقع مكتبنا في موقع متميز بدبي، وندعوك لزيارتنا ومناقشة احتياجاتك
        العقارية شخصيًا. هنا تبدأ رحلتك مع العقارات في دبي .
      </div>
    ),

    ready_dream_property: (
      <h2 className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'>
        هل أنت مستعد للعثور على{' '}
        <span className='text-[#D4AF37]'>عقار أحلامك</span>؟
      </h2>
    ),
    ready_dream_property_description: (
      <p className='text-lg text-gray-700 dark:text-gray-300 mb-8'>
        دع فريقنا الخبير يرشدك. سواء كنت تشتري عقارًا في دبي، تبيع أو تستثمر،
        نحن هنا لمساعدتك في كل خطوة بخدماتنا العقارية الشاملة.
      </p>
    ),
    personalized_service: 'خدمة شخصية',
    personalized_service_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        حلول مخصصة لاحتياجاتك الفريدة
      </p>
    ),
    market_expertise: 'خبرة السوق',
    market_expertise_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        معرفة متعمقة بسوق العقارات في دبي
      </p>
    ),
    exclusive_listings: 'قوائم حصرية',
    exclusive_listings_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        الوصول إلى عقارات مميزة غير متاحة في أي مكان آخر
      </p>
    ),

    contact_us_today: 'تواصل معنا اليوم',

    // Services Page
    our_services: 'خدماتنا',
    our_services_description:
      'بخبرة تزيد عن تسع سنوات، تُعدّ إكس-سبيس للعقارات شركة وساطة عقارية موثوقة في دبي، تُقدّم حلولاً عقارية متكاملة للمشترين والبائعين والمؤجرين والمستثمرين. نتخصص في شراء وبيع وتأجير العقارات السكنية والتجارية في أرقى أحياء دبي، بما في ذلك الأحياء ذات الملكية الحرة. تشمل خدماتنا الدعم قبل البيع وبعده، والإرشاد في مجال الاستثمار العقاري على الخارطة، والاستشارات التمويلية، وتقييم العقارات، والتسويق الاحترافي لضمان أقصى قدر من الانتشار وأفضل قيمة سوقية. كما نُقدّم المساعدة في الحصول على التأشيرة الذهبية وتحليلات استثمارية مُفصّلة لمساعدة عملائنا على اتخاذ قرارات عقارية مدروسة ومربحة. يكرّس فريقنا المُتمرّس جهوده لتقديم خدمة شخصية، ورؤى سوقية ثاقبة، ودعم شامل، لمساعدتك على تحقيق أهدافك العقارية بثقة في سوق دبي العقاري الديناميكي.',
    why_list_with_xspace: 'لماذا تختار X-Space Real Estate',
    why_list_with_xspace_description:
      'X-Space Real Estate هي شريكك الموثوق في سوق العقارات في دبي. نحن الخيار الأمثل لأصحاب العقارات والمستثمرين، نحول العقارات إلى استثمارات مربحة. بفضل خدمتنا السلسة ومعرفتنا العميقة بالسوق، نحن الشريك المثالي لجميع احتياجاتك العقارية في دبي والمناطق المحيطة.',
    extensive_network: 'شبكة واسعة',
    extensive_network_description:
      'نستفيد من شبكتنا الواسعة من الوكلاء المحترفين وشركاء التسويق لتوفير أقصى قدر من الانتشار لعقارك.',
    legal_expertise: 'الخبرة القانونية',
    legal_expertise_description:
      'يقدم فريقنا إرشادات قانونية شاملة لضمان عملية سلسة وآمنة لجميع معاملات العقارات في دبي.',
    media_resources: 'الموارد الإعلامية',
    media_resources_description:
      'نستخدم قنوات إعلامية واسعة النطاق لتعزيز ظهور عقارك وخدماتنا العقارية.',
    real_estate_services: 'الخدمات العقارية',
    service_buy: 'شراء',
    service_buy_title: 'ابحث عن عقارك المثالي في دبي',
    service_buy_description:
      'سواء كنت تبحث عن شراء عقار ليكون منزلك الحلم أو استثماراً مربحاً، نحن هنا لإرشادك. يمنحك خبراؤنا إمكانية الوصول إلى مجموعة واسعة من العقارات المعروضة للبيع في دبي، بما في ذلك العقارات الفاخرة والفلل والشقق، لضمان العثور على الأنسب لاحتياجاتك.',

    service_sell: 'بيع',
    service_sell_title: 'ضاعف فرص ظهور عقارك',
    service_sell_description:
      'يقدم فريقنا خدمات شاملة للبيع للحصول على أفضل عائد على استثمارك. نستخدم شبكتنا الواسعة ومواردنا التسويقية لتعزيز ظهور عقارك وقوائمك العقارية.',

    service_rent: 'إيجار',
    service_rent_title: 'شريكك الموثوق للإيجارات',
    service_rent_description:
      'سيرشدك خبراؤنا العقاريون خلال عملية البحث عن الإيجار المثالي. نقدم وصولاً إلى قاعدة بيانات واسعة من العقارات للإيجار في دبي، بالإضافة إلى خدمات مثل الإيجارات قصيرة الأجل، وفحص المستأجرين، وتفتيش العقارات.',

    residency_solutions: 'حلول الإقامة',
    residency_solutions_title_1: 'خدمات التأشيرة الذهبية',
    residency_solutions_title_2: 'طريقك للإقامة في الإمارات',
    residency_solutions_description:
      'نرشد عملاءنا خلال عملية الحصول على التأشيرة الذهبية في دبي. يضمن فريقنا تقديم طلب سلس من خلال توفير خيارات الاستثمار العقاري المناسبة ودعم المستندات، مما يجعل الإقامة في دبي أسهل وأكثر سهولة. نحن ندير جميع خدمات التأشيرات المتعلقة بشراء عقارك.',

    property_investment_guidance: 'إرشادات الاستثمار العقاري',
    documentation_support: 'دعم الوثائق',
    application_processing: 'معالجة الطلبات',
    legal_compliance: 'الامتثال القانوني',

    financing_solutions: 'حلول التمويل',
    financing_solutions_title_1: 'استشارات الرهن العقاري',
    financing_solutions_title_2: 'اعثر على التمويل المناسب لعقارك',
    financing_solutions_description:
      'يبسط خبراء الرهن العقاري لدينا عملية التمويل العقاري. نقدم استشارات شخصية لمساعدتك في العثور على أفضل القروض وخيارات التمويل المتاحة، مما يجعل امتلاك عقارك في دبي سهلاً وسريعاً.',

    personalized_consultations: 'استشارات مخصصة',
    best_loan_options: 'أفضل خيارات القروض',
    application_support: 'دعم الطلبات',

    ready_to_experience: (
      <h2 className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'>
        جاهز لتجربة{' '}
        <span className='text-[#D4AF37]'> خدمات العقارات الفاخرة</span>؟
      </h2>
    ),

    ready_to_experience_description:
      'بصفتنا شريكك الموثوق لجميع احتياجاتك العقارية، نقدم خدمات شاملة ومخصصة تناسب متطلباتك. اختر X-Space للحصول على دعم خبير وحلول عقارية سلسة.',

    goldenVisa: 'التأشيرة الذهبية',
    sellProperty: 'بيع العقار',
    mortgage: 'الرهن العقاري',

    // صفحة الخدمات – تأشيرة الذهبية
    uae_residency_solutions: 'حلول الإقامة في الإمارات',
    golden_visa_services: 'خدمات التأشيرة الذهبية',
    your_path: 'مسارك نحو الإقامة في الإمارات',
    your_path_description:
      'نرشد العملاء خلال عملية الحصول على تأشيرة دبي الذهبية. يضمن فريقنا تطبيقًا سلسًا عبر تقديم خيارات استثمار عقاري مناسبة في الإمارات ودعم في الوثائق، مما يجعل الإقامة في دبي أسهل وأكثر وصولاً.',
    start_application: 'ابدأ تقديم الطلب',
    uae_golden_visa_benefits: 'مزايا التأشيرة الذهبية الإماراتية',
    uae_golden_visa_benefits_description:
      'توفر التأشيرة الذهبية الإماراتية مزايا استثنائية للمستثمرين، روّاد الأعمال، والمحترفين المهرة الذين يرغبون بجعل الإمارات مقر إقامتهم الدائم.',
    ten_year_residency: 'إقامة مدتها 10 سنوات',
    ten_year_residency_description:
      'احصل على تأشيرة إقامة طويلة الأجل قابلة للتجديد لمدة عقد كامل، مما يوفر الاستقرار والأمان.',
    family_inclusion: 'ضم العائلة',
    family_inclusion_description:
      'تمدد إقامتك في الإمارات لتشمل زوجتك / زوجك، أطفالك، ووالديك، مما يسمح لعائلتك بالعيش معك في البلاد.',
    no_sponsor_required: 'لا حاجة لكفيل',
    no_sponsor_required_description:
      'تمتع بالاستقلال الكامل. التأشيرة الذهبية تمنحك إقامة دون الحاجة إلى كفيل في الإمارات، مما يمنحك حرية أكبر.',
    multiple_entry: 'متعددة الدخول',
    multiple_entry_description:
      'استمتع بالسفر دون قيود مع دخول وخروج غير محدود من الإمارات، مثالية للرحلات التجارية والشخصية.',
    property_ownership: 'امتلاك العقار',
    property_ownership_description:
      'احصل على ملكية كاملة للعقار في المناطق المخصصة للتملك الحر، وهي ميزة رئيسية للمستثمرين العقاريين في دبي والإمارات.',
    business_opportunities: 'فرص الأعمال',
    business_opportunities_description:
      'افتح الآفاق لتأسيس وامتلاك مشروع تجاري بملكية 100٪ في العديد من القطاعات المزدهرة في الإمارات.',
    golden_visa_eligibility_criteria: 'معايير أهلية التأشيرة الذهبية',
    property_investment_route: 'مسار الاستثمار العقاري',
    property_investment_route_description:
      'اكتشف معايير الأهلية للحصول على التأشيرة الذهبية الإماراتية من خلال الاستثمار الاستراتيجي أو المواهب المتخصصّة.',
    min_investment: 'الحد الأدنى للاستثمار: 2 مليون درهم',
    min_investment_description:
      'تأهل للتأشيرة الذهبية عبر شراء عقار في دبي أو أي إمارة معتمدة بسعر لا يقل عن 2 مليون درهم إماراتي.',
    freehold_properties: 'عقارات التملك الحر',
    freehold_properties_description:
      'يجب أن يكون الاستثمار في عقارات التملك الحر في الإمارات، مما يمنحك حقوق الملكية الكاملة في المناطق المعتمدة.',
    no_loan_restrictions: 'بدون قيود على القرض',
    no_loan_restrictions_description:
      'يمكن شراء العقار باستخدام الرهن العقاري، مما يوفر خيارات مرنة لاستثمارك.',
    business_investment_route: 'مسار استثمار الأعمال',
    business_investment_route_description:
      'التأهل من خلال استثمار تجاري وأنشطة ريادية في الإمارات.',
    business_investment_aed_10_million: 'استثمار أعمال: 10 ملايين درهم',
    business_investment_aed_10_million_description:
      'أمّن تأشيرتك من خلال تأسيس أو استثمار في شركات إماراتية برأس مال لا يقل عن 10 ملايين درهم.',
    specialized_talents: 'المواهب المتخصصة',
    specialized_talents_description:
      'التأشيرة الذهبية متاحة للمحترفين عاليي المهارات، بمن فيهم الأطباء، المهندسون، العلماء، والفنانون، الذين يمكنهم إظهار مساهمات استثنائية.',
    outstanding_students: 'الطلاب المتفوقون',
    outstanding_students_description:
      'الطلاب والخريجون ذوو الأداء الأكاديمي المتميز مؤهلون أيضًا، معترف به كمسار نحو الإقامة طويلة الأجل.',
    our_golden_visa_process: 'عملية التأشيرة الذهبية لدينا',
    initial_consultation: 'الاستشارة الأولية',
    initial_consultation_description:
      'نبدأ باستشارة مجانية لتقييم أهليتك وتوجيهك نحو أفضل مسار لتقديم طلب التأشيرة الذهبية في دبي حسب احتياجاتك المحددة.',
    property_selection: 'اختيار العقار',
    property_selection_description:
      'يقدّم خبراؤنا إرشادًا مخصصًا لمساعدتك في اختيار الاستثمار العقاري الأمثل في دبي الذي يستوفي تمامًا متطلبات التأشيرة الذهبية.',
    property_documentation: 'الوثائق',
    property_documentation_description:
      'نقدّم دعمًا شاملاً في إعداد كافة الوثائق المطلوبة، التصديق، وتقديمها للجهات الحكومية المعنية. نضمن أن يتم التعامل مع كل التفاصيل بدقة.',
    visa_approval: 'موافقة التأشيرة',
    visa_approval_description:
      'متابعة حالة الطلب وتقديم الدعم حتى الموافقة النهائية وإصدار التأشيرة.',
    expert_guidance: 'إرشاد خبراء',
    expert_guidance_description: 'لماذا تختار X-Space لتأشيرتك الذهبية؟',
    expert_guidance_description_1:
      'بخبرة تزيد عن 9 سنوات في سوق العقارات بدبي ومعرفة عميقة بأنظمة التأشيرة الذهبية في الإمارات، نقدّم دعمًا شاملًا لمسار إقامتك.',
    expert_guidance_description_2:
      'معرفة متخصصة بقوانين التأشيرات الإماراتية: نحن نختص بأحدث متطلبات التأشيرة الذهبية في دبي، مما يضمن أن يكون طلبك متوافقًا وناجحًا.',
    expert_guidance_description_3:
      'إرشاد شامل للاستثمار العقاري: يساعدك خبراؤنا في العثور على أفضل العقارات التي تستوفي الأهلية للتأشيرة الذهبية، مع تعظيم العائد من استثمارك.',
    expert_guidance_description_4:
      'دعم كامل في الوثائق: نتولى كل الأعمال الورقية اللازمة، لضمان تقديم الطلب بسلاسة وبالدقة المطلوبة.',
    expert_guidance_description_5:
      'إدارة مخصصة لطلبات التأشيرة: ندير عملية تأشيرتك الذهبية بالكامل من الاستشارة الأولية حتى الموافقة النهائية.',
    expert_guidance_description_6:
      'خدمات ما بعد الموافقة: يستمر دعمنا بعد الموافقة على التأشيرة، ونقدّم المساعدة في الاستقرار والتكامل داخل الإمارات.',
    expert_guidance_description_7:
      'مساعدة في تأشيرات العائلة: نقدّم دعمًا كاملاً لمساعدة زوجتك / زوجك، أطفالك، ووالديك أيضًا على الحصول على تأشيرة العائلة الإماراتية.',
    expert_guidance_description_8:
      'الامتثال القانوني والاستشارات: فريقنا يقدّم استشارات قانونية خبيرة لضمان أن استثمارك للتأشيرة الذهبية متوافق تمامًا مع قانون الإمارات.',
    expert_guidance_description_9:
      'الدعم المستمر والاستشارة: نحن دائمًا متاحون للاستشارات والدعم المستمر لتجربتك مع التأشيرة الذهبية، مما يمنحك راحة البال طوال هذه الرحلة.',
    ready_to_secure: 'هل أنت مستعد لتأمين تأشيرتك الذهبية الإماراتية؟',
    ready_to_secure_description:
      'اخطُو الخطوة الأولى نحو الإقامة الإماراتية. خبراؤنا مستعدون لإرشادك خلال كامل عملية التأشيرة الذهبية ومساعدتك في العثور على الاستثمار العقاري المثالي.',
    golden_visa_consultation: 'استشارة التأشيرة الذهبية',

    // صفحة الخدمات – بيع العقارات
    property_selling: 'خدمات بيع العقارات',
    sell_your_property: 'بع عقارك',
    maximize_your_property: 'حقق أقصى قدر من ظهور عقارك في دبي',
    maximize_your_property_description:
      'يقدّم فريقنا خدمات شاملة لبيع العقارات لضمان أفضل عائد على استثمارك. نستخدم شبكتنا الواسعة ومواردنا التسويقية القوية لمنح عقارك أقصى قدر من الظهور، مما يضمن أن تكون عملية إدارة وبيع العقار سلسة وناجحة.',
    get_free_valuation: 'احصل على تقييم مجاني للعقار',
    why_choose_x_space: 'لماذا تختار X-Space لبيع عقارك؟',
    why_choose_x_space_description:
      'بخبرة تزيد عن 9 سنوات في سوق العقارات بدبي، نقدم خدمات بيع عقارات لا مثيل لها تعظم قيمة منزلك وتقلل مدة بقائه في السوق.',
    maximize_exposure: 'أقصى ظهور',
    maximize_exposure_description:
      'نستفيد من شبكتنا الواسعة وقنواتنا التسويقية القوية لتحقيق أقصى قدر من وضوح القوائم العقارية، مما يضمن وصول منزلك إلى جمهور واسع من المشترين المؤهلين.',
    expert_valuation: 'تقييم خبير',
    expert_valuation_description:
      'احصل على تقييمات دقيقة وموثوقة للعقار بناءً على اتجاهات السوق الحالية والمبيعات المماثلة في منطقتك، مما يساعدك على تسعير عقارك بشكل صحيح.',
    professional_marketing: 'تسويق احترافي',
    professional_marketing_description:
      'تشمل خدماتنا التصوير الفوتوغرافي عالي الجودة، والجولات الافتراضية التفاعلية، وعروض القوائم المميزة لجعل عقارك بارزًا في السوق.',
    dedicated_support: 'دعم مخصص',
    dedicated_support_description:
      'سيكون لديك وكيل شخصي يقدم دعمًا مخصصًا طوال عملية بيع العقار، من الإدراج الأولي إلى الإغلاق النهائي.',
    our_proven_selling_process: 'عملية البيع المجربة لدينا',
    property_evaluation: 'تقييم العقار',
    property_evaluation_description:
      'نبدأ بتحليل شامل للسوق العقاري وتقييم دقيق للعقار لتحديد استراتيجية التسعير المثالية لإدراجك.',
    marketing_strategy: 'استراتيجية التسويق',
    marketing_strategy_description:
      'تشمل استراتيجيتنا التصوير الاحترافي، الجولات الافتراضية التفاعلية، وحملة تسويقية متعددة القنوات لمنح عقارك أقصى قدر من الظهور.',
    buyer_screening: 'فرز المشترين',
    buyer_screening_description:
      'نتولى عملية الفرز المسبق لجميع المشترين المحتملين، مع تحديد الأشخاص المؤهلين لضمان عملية شراء جدية وسلسة.',
    negotiation: 'التفاوض',
    negotiation_description:
      'يقدّم وكلاؤنا ذوو الخبرة مفاوضات احترافية لتأمين أفضل سعر وشروط مناسبة لبيع عقارك.',
    documentation: 'الوثائق',
    documentation_description:
      'ندير جميع المستندات القانونية لضمان الامتثال الكامل للوائح العقارات في دبي، لعملية خالية من المتاعب.',
    closing: 'الإغلاق',
    closing_description:
      'يقدّم فريقنا دعمًا كاملاً خلال عملية التحويل النهائية، لضمان إغلاق سلس وناجح لبيع عقارك في دبي.',
    comprehensive_services: 'خدمات شاملة',
    whats_included: 'ما الذي يتضمنه خدمتنا',
    whats_included_description:
      'تشمل خدمتنا الشاملة كل ما تحتاجه لبيع عقارك في دبي بنجاح وبأفضل سعر ممكن في أقصر وقت.',
    whats_included_description_1:
      'تقييم عقاري مجاني احترافي: احصل على تقييم عقاري مجاني ودقيق لتحديد السعر الأمثل لإدراج عقارك.',
    whats_included_description_2:
      'تصوير عالي الجودة وجولات افتراضية: نقوم بإنشاء صور مميزة وجولات افتراضية تفاعلية لجعل قائمتك العقارية تبرز.',
    whats_included_description_3:
      'حملة تسويق متعددة المنصات: سيتم الترويج لعقارك عبر حملة متعددة القنوات لتحقيق أقصى قدر من الظهور وجذب المشترين المؤهلين.',
    whats_included_description_4:
      'فرز المشترين المؤهلين والجولات: نتولى جميع استفسارات المشترين ونفرز العملاء المحتملين مسبقًا لضمان النظر فقط في العروض الجادة.',
    whats_included_description_5:
      'مفاوضات احترافية وتحسين الأسعار: يتفاوض فريقنا الخبير نيابةً عنك لضمان أفضل سعر وشروط لبيع عقارك.',
    whats_included_description_6:
      'دعم كامل في الوثائق القانونية: ندير جميع الأعمال الورقية القانونية ونضمن الامتثال الكامل للوائح العقارات في دبي لعملية سلسة.',
    whats_included_description_7:
      'إدارة المعاملات ودعم الإغلاق: نقدم دعمًا شاملاً حتى عملية الإغلاق النهائية لضمان بيع سلس وفي الوقت المناسب.',
    whats_included_description_8:
      'دعم ومساعدة بعد البيع: خدماتنا لا تنتهي عند الإغلاق؛ نقدم دعمًا مستمرًا لأي احتياجات بعد البيع.',
    start_selling_process: 'ابدأ عملية البيع',
    ready_to_sell_your_property: 'هل أنت مستعد لبيع عقارك؟',
    get_free_property_valuation: 'احصل على تقييم مجاني للعقار',
    ready_to_sell_your_property_description:
      'ابدأ بتقييم مجاني للعقار واستشارة. خبراؤنا جاهزون لمساعدتك على تعظيم قيمة عقارك وضمان تجربة بيع سلسة.',

    // صفحة الخدمات – استشارات الرهن العقاري
    financing_solutions: 'حلول التمويل',
    mortgage_consultancy: 'استشارات الرهن العقاري',
    find_the_right_financing: 'اعثر على التمويل المناسب لعقارك',
    find_the_right_financing_description:
      'يبسط خبراء الرهن العقاري لدينا عملية تمويل المنازل. نقدم استشارات شخصية لمساعدتك في العثور على أفضل القروض وخيارات التمويل المتاحة، مما يسهل عليك الحصول على عقارك المثالي في دبي.',
    get_pre_approved: 'احصل على موافقة مبدئية',
    our_mortgage_services: 'خدمات الرهن العقاري لدينا',
    our_mortgage_services_description:
      'حلول رهن عقاري شاملة مصممة خصيصًا لاحتياجاتك المالية وأهدافك الاستثمارية في سوق العقارات الديناميكي بدبي.',
    mortgage_pre_approval: 'الموافقة المبدئية على الرهن العقاري',
    mortgage_pre_approval_description:
      'احصل على موافقة مبدئية على الرهن العقاري لتعزيز موقفك الشرائي وتبسيط عملية الشراء.',

    investment_calculator: 'حاسبة الاستثمار',
    investment_calculator_description:
      'احسب العائد المحتمل باستخدام حاسبة الاستثمار العقاري الشاملة لدينا.',
    dubai_real_estate_investments: 'حاسبة استثمارات العقارات في دبي',
    dubai_real_estate_investments_description:
      'حلل وحسب وقيم العائد المحتمل على العقارات',
    dubai_real_estate_investments_hold: 'الاحتفاظ',
    dubai_real_estate_investments_flip: 'البيع السريع',
    expected_capital_appreciation: 'الزيادة المتوقعة في رأس المال',
    expected_total_rental_income: 'إجمالي الدخل الإيجاري',
    expected_aed: 'درهم',
    expected_usd: 'دولار أمريكي',
    expected_investment_amount: 'أدخل مبلغ الاستثمار',
    expected_annual_appreciation: 'العائد السنوي المتوقع',
    expected_annual_rental_yield: 'العائد الإيجاري السنوي المتوقع',
    expected_holding_period: 'مدة الاحتفاظ (بالسنوات)',
    expected_value_after: 'القيمة بعد',
    expected_value_years: 'سنوات',
    expected_value_exit_fee: 'رسوم الخروج (2.5%)',
    expected_value_annual_cash_flow: 'التدفق النقدي السنوي',
    expected_value_entry_fee: 'رسوم الدخول (1.5%)',
    expected_reset: 'إعادة تعيين',
    expected_investment_duration: 'مدة الاستثمار:',
    expected_expected_irr: 'العائد الداخلي المتوقع (IRR)',
    expected_exit_multiple: 'مضاعف الخروج',
    expected_project_duration: 'مدة المشروع (بالسنوات)',
    expected_total_roi: 'إجمالي العائد على الاستثمار',
    expected_flip_entry_fee: 'رسوم الدخول (2.5%)',
    expected_flip_exit_fee: 'رسوم الخروج (2.5%)',

    rate_comparison: 'مقارنة الأسعار',
    rate_comparison_description:
      'قارن أسعار الفائدة على الرهن العقاري من عدة بنوك ومؤسسات مالية للحصول على أفضل صفقة.',
    loan_structuring: 'هيكلة القروض',
    loan_structuring_description:
      'نصائح خبراء حول هيكلة القروض، الشروط، وخيارات السداد بما يتناسب مع وضعك المالي.',
    documentation_support: 'دعم الوثائق',
    documentation_support_description:
      'مساعدة كاملة في مستندات الرهن العقاري، ومعالجة الطلبات، والتقديم.',
    fast_processing: 'معالجة سريعة',
    fast_processing_description:
      'معالجة سريعة للرهون العقارية بفضل علاقاتنا المصرفية الراسخة وإجراءاتنا المبسطة.',
    expert_guidance: 'إرشاد الخبراء',
    expert_guidance_description:
      'استشارات شخصية يقدمها متخصصون معتمدون في الرهن العقاري يتمتعون بمعرفة عميقة بالسوق.',
    mortgage_types_available: 'أنواع الرهن العقاري المتاحة',
    conventional_mortgage: 'الرهون العقارية التقليدية',
    conventional_mortgage_description:
      'خيارات تقليدية بمعدلات تنافسية وشروط مرنة لشراء العقارات في دبي.',
    fixed_rate_mortgage: 'الرهون العقارية ذات السعر الثابت',
    fixed_rate_mortgage_description: 'دفعات شهرية مستقرة بأسعار فائدة ثابتة',
    variable_rate_mortgage: 'الرهون العقارية ذات السعر المتغير',
    variable_rate_mortgage_description: 'معدلات مرنة تتغير مع ظروف السوق',
    up_to_80_financing: 'تمويل يصل إلى 80%',
    up_to_80_financing_description: 'نسب تمويل مرتفعة للمشترين المؤهلين',
    islamic_mortgage: 'الرهون العقارية الإسلامية',
    islamic_mortgage_description:
      'حلول تمويل متوافقة مع الشريعة الإسلامية والقوانين المصرفية الإسلامية.',
    murabaha_financing: 'تمويل المرابحة',
    murabaha_financing_description:
      'هيكل تمويل بالمرابحة متوافق مع الشريعة الإسلامية',
    ijara_financing: 'تمويل الإجارة',
    ijara_financing_description:
      'ترتيبات إيجار تنتهي بالتمليك مع نقل الملكية تدريجياً',
    sharia_compliance: 'التوافق مع الشريعة',
    sharia_compliance_description: 'متوافق تمامًا مع مبادئ الصيرفة الإسلامية',
    our_mortgage_process: 'عملية الرهن العقاري لدينا',
    initial_consultation: 'الاستشارة الأولية',
    initial_consultation_description_mortgage:
      'استشارة مجانية لتقييم وضعك المالي وتحديد أفضل خيارات الرهن العقاري.',
    pre_approval: 'الموافقة المبدئية',
    pre_approval_description:
      'احصل على موافقة مبدئية لفهم ميزانيتك وتعزيز عرضك.',
    application_processing: 'معالجة الطلب',
    application_processing_description:
      'إكمال طلب الرهن العقاري بمساعدتنا وتقديمه إلى المقرضين المفضلين.',
    approval_closing: 'الموافقة والإغلاق',
    approval_closing_description:
      'الموافقة النهائية، تقييم العقار، وعملية الإغلاق بدعم كامل.',
    expert_mortgage_services: 'خدمات الرهن العقاري المتخصصة',
    expert_mortgage_services_description:
      'لماذا تختار استشارات الرهن العقاري لدينا؟',
    expert_mortgage_services_description_1:
      'بخبرة واسعة في سوق الرهن العقاري بدبي وعلاقات قوية مع البنوك الرائدة، نقدم حلول رهن عقاري شاملة مصممة خصيصًا لاحتياجاتك.',
    expert_mortgage_services_description_2:
      'الوصول إلى أكثر من 15 بنكًا ومُقرضًا رائدًا',
    expert_mortgage_services_description_3: 'أسعار فائدة وشروط تنافسية',
    expert_mortgage_services_description_4: 'عملية موافقة مبدئية سريعة',
    expert_mortgage_services_description_5: 'تحليل ونصائح مالية متخصصة',
    expert_mortgage_services_description_6: 'دعم كامل في الوثائق',
    expert_mortgage_services_description_7: 'حلول رهن عقاري مخصصة',
    expert_mortgage_services_description_8: 'دعم وإرشاد بعد الموافقة',
    expert_mortgage_services_description_9: 'بدون رسوم أو تكاليف مخفية',
    ready_to_secure_mortgage: 'هل أنت مستعد للحصول على رهن عقاري؟',
    ready_to_secure_mortgage_description:
      'ابدأ باستشارة مجانية للرهن العقاري والموافقة المبدئية. خبراؤنا جاهزون لمساعدتك في العثور على أفضل حل تمويلي لشراء عقارك.',
    mortgage_consultation: 'استشارة رهن عقاري',
  },
  zh: {
    // Navigation
    aboutUs: '关于我们',
    properties: '房产',
    projects: '项目',
    buy: '购买',
    rent: '租赁',
    offPlanResale: '期房转售',
    offPlan: '期房项目',
    commercial: '商业',
    properties: '房产',
    newsArticles: '新闻 / 文章',
    newsArticles: '新闻 / 文章',
    blog: '博客',
    agent: '经纪人',
    contactUs: '联系我们',

    // Hero Section
    heroTitle: '在迪拜找到您的梦想房产',
    heroSubtitle: '探索迪拜最负盛名地区的豪华房产',

    // Property Search
    searchPlaceholder: '城市、社区或建筑',
    propertyType: '房产类型',
    bedsAndBaths: '卧室 & 卫浴',
    search: '搜索',
    noResultsFound: '未找到结果',
    tryDifferentSearch: '尝试不同的搜索词或浏览我们的房产',
    clearSearch: '清除搜索',

    // Categories
    exploreServices: '探索我们的服务',
    buyProperties: '购买房产',
    buyDescription: '从我们精心挑选的房产中找到您的理想家园。',
    rentProperties: '租赁房产',
    rentDescription: '探索迪拜最受欢迎地区的高端出租房产。',
    offPlanProjects: '期房项目',
    offPlanDescription: '通过我们的独家期房项目投资迪拜的未来。',
    explore: '探索',

    // Featured Properties
    featuredProperties: '精选房产',
    featuredDescription: '发现我们精心挑选的迪拜最独特房产',
    viewDetails: '查看详情',
    viewAllProperties: '查看所有房产',

    // Agents Section
    meetOurAgents: '认识我们的专业经纪人',
    realEstateAgents: '房地产经纪人',
    agentsExperience:
      '我们的专业经纪人在迪拜房地产市场拥有多年的经验，随时准备帮助您找到梦想中的房产。凭借对本地市场的深入了解和各类房产的专业知识，他们将引导您完成房地产旅程的每一步。',
    buySellInvest:
      '无论您是想在迪拜充满活力的房地产市场中买房、卖房，还是投资，我们的经纪人都致力于为您提供量身定制的个性化服务，以满足您的特定需求和偏好。',
    yearsExperience: '年经验',
    propertiesSold: '已售房产',
    contactAgent: '联系经纪人',
    viewProfile: '查看资料',
    meetAllAgents: '查看所有经纪人',
    showAllAgents: '显示全部',
    specialization: '专业领域',
    languages: '语言',
    contactNow: '立即联系',

    // Top Sales Section
    topSalesAgents: '顶级销售经纪人',
    topSalesDescription: '表彰我们表现最优秀的经纪人',
    filterByPeriod: '按时间筛选',
    month: '月',
    quarter: '季度',
    year: '年',
    salesVolume: '销售额',
    transactions: '交易量',
    ranking: '排名',

    // Awards & Recognition
    awardsRecognition: '我们的奖项与认可',
    awardsDescription: '我们为在房地产服务和客户满意度方面的卓越表现感到自豪。',
    scrollToExplore: '滑动以浏览所有奖项"',
    realEstateExcellence: '房地产卓越',

    // Partner Developers
    partnerDevelopers: '合作开发商',
    partnerDevelopersDescription:
      '我们与迪拜最知名的开发商合作，为您带来卓越的房产和投资机会。',
    visitWebsite: '访问网站',

    // Why Choose Us
    whyChooseUs: '为什么选择 X Space',
    whyChooseUsDescription:
      '我们致力于为您提供卓越的服务和量身定制的房地产解决方案。',
    marketExpertise: '市场专业知识',
    marketExpertiseDescription:
      '我们的团队深入了解迪拜房地产市场，确保您做出明智的投资决策。',
    trustedAdvisors: '值得信赖的顾问',
    trustedAdvisorsDescription:
      '凭借数千笔成功交易，我们建立了诚信、透明和客户满意度的良好声誉。',
    timeEfficiency: '高效服务',
    timeEfficiencyDescription:
      '我们珍惜您的时间，并简化房产搜索流程，让您快速找到理想房产。',
    awardWinningService: '屡获殊荣的服务',
    awardWinningServiceDescription:
      '凭借卓越的房地产服务，我们始终超越客户的期望。',

    // News & Insights
    newsInsights: '新闻与洞察',
    newsInsightsDescription:
      '随时了解迪拜房地产市场的最新新闻、趋势和行业洞察。',
    latestNews: '最新新闻',
    readMore: '阅读更多',
    featuredBlogPosts: '精选博客文章',
    viewAll: '查看全部',
    browseAllArticles: '浏览所有文章',

    // FAQ
    faq: '常见问题',
    faqDescription: '查找有关在迪拜买卖和投资房地产的常见问题解答。',
    cantFindAnswer: '找不到您要的答案？',
    contactOurTeam: '联系我们的团队',

    // Call to Action
    readyToFindProperty: '准备好找到您的梦想房产了吗？',
    readyToFindPropertyDescription:
      '立即联系我们的专业团队，让我们帮助您找到符合您需求和预算的完美迪拜房产。',
    contactUsNow: '立即联系我们',
    viewMoreProperties: '查看更多房产',

    // Testimonials
    clientTestimonials: '客户评价',
    clientTestimonialsDescription:
      '阅读我们满意客户的评价，了解他们与 X Space Real Estate 的合作体验。',

    // Property Details
    bedrooms: '卧室',
    bathrooms: '浴室',
    reset: '重置',
    ok: '确定',
    select: '选择',
    beds: '卧室',
    baths: '浴室',
    commercial: '商业',

    // Time periods
    january: '一月',
    february: '二月',
    march: '三月',
    april: '四月',
    may: '五月',
    june: '六月',
    july: '七月',
    august: '八月',
    september: '九月',
    october: '十月',
    november: '十一月',
    december: '十二月',
    q1: '第一季度',
    q2: '第二季度',
    q3: '第三季度',
    q4: '第四季度',

    // Footer
    footerDescription:
      '您值得信赖的迪拜房地产合作伙伴，提供卓越的房产和个性化服务。',
    company: '公司',
    properties: '房产',
    contactInfo: '联系方式',
    allRightsReserved: '版权所有。',
    privacyPolicy: '隐私政策',
    termsOfService: '服务条款',
    cookiesPolicy: 'Cookie 政策',
    careers: '职业机会',
    services: '服务',
    newProjects: '新项目',
    workingHours: '周一至周六，9:30 - 18:30',
    location: '迪拜，阿联酋',

    atXSpace_1: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        在{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-Space 房地产，
        </span>{' '}
        我们是一家 迪拜值得信赖的房地产中介
        致力于让房地产对客户来说既可获取又有利可图。我们不断拓展房地产卓越的边界，
        将找到理想住宅或投资的梦想变为现实。
      </p>
    ),
    atXSpace_2: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'>
        作为 迪拜房地产专家， 我们在这里帮助您在房地产的未来中占据一席之地。
      </p>
    ),

    learnMore: '了解更多',
    yearsExperience: '多年经验',
    companyName: 'X - SPACE 房地产',
    welcometoXSpace: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        欢迎来到{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-Space 房地产公司，
        </span>{' '}
        您购买、出售和投资新领域的首选目的地。我们提供专业的房地产服务，
        并专注于为客户对接市场中的优质机会。
      </p>
    ),
    lookingToAcquire: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        无论您是想{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          在迪拜购买、出售或租赁房产，{' '}
        </span>
        开发新项目，还是投资独特资产，我们的专业团队都将全程为您提供指导。
      </p>
    ),
    company_detail_1: (
      <p className=' text-justify'>
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-SPACE 房地产
        </span>{' '}
        前身为“良好声誉房地产经纪公司”，是一家总部位于迪拜的领先房地产中介与物业管理公司。
        自2017年成立以来，我们已建立了提供优质服务和卓越客户体验的良好声誉。
        我们敬业的 多语种迪拜房地产经纪团队 致力于为您找到符合需求的理想房产。
      </p>
    ),
    company_detail_2: (
      <p className=' text-justify'>
        每年，我们处理超过100亿迪拉姆的房地产交易。我们是您
        值得信赖的房地产合作伙伴
        与阿联酋顶尖开发商合作，确保客户获得最佳的房产和服务。我们的
        智能房地产投资 方法基于深厚的市场知识、严谨的研究，以及个性化的建议。
      </p>
    ),
    company_detail_3: (
      <p className=' text-justify'>
        在 X-SPACE ，诚信、卓越、创新和尊重是我们的一切核心价值观。
        我们严格遵守阿联酋法律法规，确保每笔交易的专业性和诚信度。
      </p>
    ),
    our_visionary_leaders: '我们的远见领导者',
    our_visionary_description: (
      <div>
        <p className='text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto'>
          认识这些创始人，他们以创新的方式和对卓越的不懈追求正在革新房地产行业。
        </p>
      </div>
    ),

    scroll_to_explore_agents: '滑动以探索所有代理',

    our_values: '我们的价值观',
    our_value_1: (
      <p className='text-gray-700 dark:text-gray-300 text-lg max-w-5xl mx-auto'>
        我们的核心价值观指导着我们的一切，塑造公司文化并影响我们在
        迪拜房地产市场 如何服务客户。我们与 多元化的迪拜房地产团队
        一起工作，确保提供顶级服务。
      </p>
    ),
    integrity: '诚信',
    integrity_description: <div>我们通过诚实和透明的商业实践来建立信任。</div>,
    excellence: '卓越',
    excellence_description: (
      <div>
        我们努力实现 房地产服务的卓越，超越期望 于我们处理的每一个房产。
      </div>
    ),
    innovation: '创新',
    innovation_description: (
      <div>我们通过创造性思维和 技术驱动的房地产服务 提供创新解决方案。</div>
    ),
    respect: '尊重',
    respect_description: (
      <div>我们重视多样化的观点，并以尊严和尊重对待每个人。</div>
    ),
    collaboration: '协作',
    collaboration_description: (
      <div>我们相信共同合作以为客户和团队实现卓越成果。</div>
    ),
    responsibility: '责任',
    responsibility_description: (
      <div>
        我们致力于道德实践，并为自己的行为负责，展示我们在{' '}
        迪拜房地产市场的深厚专业知识。
      </div>
    ),
    our_dynamic_team: '我们充满活力的团队',
    our_dynamic_description: (
      <div>
        <p className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
          认识这些敬业的专业人士，他们让 X-Space 房地产成为行业领导者。
        </p>
      </div>
    ),
    values: '价值观',
    values_description_1: (
      <p>
        在我们的运营核心，我们优先考虑清晰沟通、奉献和卓越的客户服务。
        通过倾听和深入理解客户需求，我们提供全面的房地产指导。我们提供
        创新的房地产解决方案
        并以致力于提供卓越成果为荣，提升您在迪拜及国际上的投资价值。
      </p>
    ),
    values_description_2: (
      <p>
        在每一次互动中，我们努力实现 迪拜房地产的卓越
        并建立基于信任和尊重的长期客户关系。每一次互动都体现出专业和诚信，
        为您提供 定制的房地产建议 的全程支持。
      </p>
    ),
    vision_and_mission: '愿景与使命',
    vision: '我们的愿景',
    vision_description: (
      <p>
        我们的目标是成为该地区领先的房地产服务提供商，以及房地产专业人士的首选。
        我们致力于与客户建立长期关系，基于 透明的房地产交易
        和相互信任，秉持最高标准的 专业精神。
      </p>
    ),
    mission: '我们的使命',
    mission_description: (
      <p>
        我们通过营造一个充满活力、乐观和高绩效的工作环境来实现愿景，
        注重建立长期合作关系。我们的成功以为客户提供的卓越成果为衡量标准。
        我们结合进取的心态与个性化关注和卓越的客户服务，指导您完成 房地产买卖
        并帮助您获得 有价值的房地产投资。
      </p>
    ),
    visit_our_office: '参观我们的办公室',
    visit_our_office_description: (
      <div className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
        我们的办公室位于迪拜的便利位置，欢迎您前来与我们面对面交流房产需求。
        在这里开启您的 迪拜房地产之旅。
      </div>
    ),

    ready_dream_property: (
      <h2 className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'>
        准备好寻找您的 <span className='text-[#D4AF37]'>梦想房产</span>了吗？
      </h2>
    ),
    ready_dream_property_description: (
      <p className='text-lg text-gray-700 dark:text-gray-300 mb-8'>
        让我们的专家团队为您提供指导。无论您是 在迪拜买房，
        卖房，还是投资，我们都将通过全面的 房地产服务 全程协助您。
      </p>
    ),
    personalized_service: '个性化服务',
    personalized_service_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        根据您的独特需求量身定制解决方案
      </p>
    ),
    market_expertise: '市场专长',
    market_expertise_description: (
      <p className='text-gray-600 dark:text-gray-400'>深入了解迪拜房地产市场</p>
    ),
    exclusive_listings: '独家房源',
    exclusive_listings_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        获取其他地方无法提供的优质房产
      </p>
    ),

    contact_us_today: '立即联系我们',

    // Services Page
    our_services: '我们的服务',
    our_services_description:
      'X-Space Real Estate 拥有超过 9 年的经验，是迪拜值得信赖的房地产经纪公司，为买家、卖家、房东和投资者提供全方位的房产解决方案。我们专注于迪拜黄金地段和永久产权社区的住宅和商业地产的买卖和租赁。我们的服务包括售前和售后支持、期房投资指导、抵押贷款咨询、房产估值和专业营销，以确保房产获得最大曝光度和最佳市场价值。我们还提供黄金签证协助和详细的投资分析，帮助客户做出明智且有利可图的房地产决策。我们经验丰富的团队致力于提供个性化服务、市场洞察和全程支持，帮助您在充满活力的迪拜房地产市场中自信地实现您的房地产目标。',
    why_list_with_xspace: '为什么选择 X-Space Real Estate',
    why_list_with_xspace_description:
      'X-Space Real Estate 是您在迪拜房地产市场值得信赖的合作伙伴。我们是房主和投资者的首选，将房产转化为有利可图的投资。我们无缝的服务和深厚的市场知识，使我们成为您在迪拜及周边地区房地产需求的理想伙伴。',
    extensive_network: '广泛的网络',
    extensive_network_description:
      '我们利用庞大的专业经纪人和营销合作伙伴网络，为您的房产提供最大程度的曝光。',
    legal_expertise: '法律专业知识',
    legal_expertise_description:
      '我们的团队提供全面的法律指导，确保迪拜所有房地产交易的顺利进行。',
    media_resources: '媒体资源',
    media_resources_description:
      '我们利用广泛的媒体渠道来最大化您房产和房地产服务的曝光度。',
    real_estate_services: '房地产服务',
    service_buy: '购买',
    service_buy_title: '在迪拜找到理想的房产',
    service_buy_description:
      '无论您是寻找梦想之家还是盈利性投资，我们都会为您提供指导。我们的专家为您提供迪拜范围广泛的待售房产，包括豪宅、别墅和公寓，确保您找到最合适的选择。',

    service_sell: '出售',
    service_sell_title: '最大化您的房产曝光',
    service_sell_description:
      '我们的团队提供全面的销售服务，以确保您获得最佳投资回报。我们利用庞大的网络和营销资源，为您的房产和房源提供最大化的曝光。',

    service_rent: '租赁',
    service_rent_title: '您值得信赖的租赁伙伴',
    service_rent_description:
      '我们的房地产专家将指导您完成寻找理想租赁房的过程。我们提供对迪拜大量租赁房源的访问，并提供短租、租客筛选和房产检查等服务。',

    residency_solutions: '居留解决方案',
    residency_solutions_title_1: '黄金签证服务',
    residency_solutions_title_2: '您在阿联酋的居留之路',
    residency_solutions_description:
      '我们为客户提供迪拜黄金签证申请的全程指导。我们的团队通过提供合适的房地产投资选择和文件支持，确保申请过程顺利，使迪拜居留更加轻松和便捷。我们处理所有与房地产购买相关的签证服务。',

    property_investment_guidance: '房地产投资指导',
    documentation_support: '文件支持',
    application_processing: '申请处理',
    legal_compliance: '法律合规',

    financing_solutions: '融资解决方案',
    financing_solutions_title_1: '按揭咨询',
    financing_solutions_title_2: '为您的房产找到合适的融资',
    financing_solutions_description:
      '我们的按揭专家简化了购房融资流程。我们提供个性化咨询，帮助您找到最优贷款和融资方案，使您轻松获得在迪拜的理想房产。',

    personalized_consultations: '个性化咨询',
    best_loan_options: '最佳贷款方案',
    application_support: '申请支持',

    ready_to_experience: (
      <h2 className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'>
        准备好体验 <span className='text-[#D4AF37]'>高端房地产服务</span>了吗？
      </h2>
    ),

    ready_to_experience_description:
      '作为您值得信赖的房地产合作伙伴，我们为您提供量身定制的综合服务，满足您的独特需求。选择 X-Space，享受专家支持和无缝的房地产解决方案。',

    goldenVisa: '黄金签证',
    sellProperty: '出售房产',
    mortgage: '按揭贷款',

    // 服务页 — 黄金签证
    uae_residency_solutions: '阿联酋居留解决方案',
    golden_visa_services: '黄金签证服务',
    your_path: '您在阿联酋的居留之路',
    your_path_description:
      '我们引导客户完成迪拜黄金签证的申请流程。团队通过提供合适的阿联酋房产投资选项及文件支持，确保申请顺畅，使在迪拜居留变得更加容易可行。',
    start_application: '开始申请',
    uae_golden_visa_benefits: '阿联酋黄金签证的好处',
    uae_golden_visa_benefits_description:
      '阿联酋黄金签证为希望将阿联酋作为长期居所的投资者、企业家和高技能专业人士提供卓越的福利。',
    ten_year_residency: '十年居留',
    ten_year_residency_description:
      '获得为期十年、可续签的长期居留签证，提供稳定与安全保障。',
    family_inclusion: '家庭纳入',
    family_inclusion_description:
      '将您的阿联酋居留延伸至配偶、子女和父母，让您的家人在这里与您共同生活。',
    no_sponsor_required: '无需担保人',
    no_sponsor_required_description:
      '享受完全的独立性。黄金签证允许您在无需阿联酋担保人的情况下居留，为您带来更大的自由。',
    multiple_entry: '多次出入境',
    multiple_entry_description:
      '可无限次出入阿联酋，适合商务和个人旅行，无任何限制。',
    property_ownership: '物业所有权',
    property_ownership_description:
      '在指定的自由持有区获得完整的物业所有权，这是迪拜及阿联酋房地产投资者的一大优势。',
    business_opportunities: '商业机会',
    business_opportunities_description:
      '开启在多个繁荣行业中100%拥有企业的潜力，在阿联酋建立并拥有您的业务。',
    golden_visa_eligibility_criteria: '黄金签证资格标准',
    property_investment_route: '房产投资路线',
    property_investment_route_description:
      '通过战略投资或专业人才的方式，探索获取阿联酋黄金签证的资格标准。',
    min_investment: '最低投资额：AED 200 万',
    min_investment_description:
      '通过在迪拜或其他指定酋长国购买价值至少 AED 200 万的房产，获得黄金签证资格。',
    freehold_properties: '自由持有物业',
    freehold_properties_description:
      '投资必须是在阿联酋自由持有的房产中，在经批准区域内授予您完全所有权权利。',
    no_loan_restrictions: '无贷款限制',
    no_loan_restrictions_description:
      '物业可以通过按揭购入，为您的投资提供灵活选项。',
    business_investment_route: '商业投资路线',
    business_investment_route_description:
      '通过在阿联酋的商业投资和创业活动获得资格。',
    business_investment_aed_10_million: '商业投资：AED 1000 万',
    business_investment_aed_10_million_description:
      '通过在阿联酋成立或投资企业，投入最低资本为 AED 1000 万，以获取签证资格。',
    specialized_talents: '专门人才',
    specialized_talents_description:
      '黄金签证适用于高度专业人士，包括医生、工程师、科学家和艺术家，他们可以展示卓越的贡献。',
    outstanding_students: '杰出学生',
    outstanding_students_description:
      '优秀学生和毕业生亦有资格，学术卓越被认作为长期居留的路径。',
    our_golden_visa_process: '我们的黄金签证流程',
    initial_consultation: '初步咨询',
    initial_consultation_description:
      '我们从免费咨询开始，评估您的资格，并根据您的具体需求，为您指引最佳的迪拜黄金签证申请路径。',
    property_selection: '房产选择',
    property_selection_description:
      '我们的专家提供定制指导，帮助您选择完全符合黄金签证要求的迪拜理想房产投资。',
    property_documentation: '文件准备',
    property_documentation_description:
      '我们提供全方位帮助，处理所有必要文件的准备、公证与提交到相关政府部门。我们确保每一个细节都正确无误。',
    visa_approval: '签证批准',
    visa_approval_description: '跟进申请状态并支持直至最终批准及签证颁发。',
    expert_guidance: '专家指导',
    expert_guidance_description: '为什么选择 X-Space 为您的黄金签证？',
    expert_guidance_description_1:
      '在迪拜房地产市场拥有 9 年以上经验，并对阿联酋黄金签证法规有深入了解，我们为您的居留之旅提供端到端支持。',
    expert_guidance_description_2:
      '对阿联酋签证法规的专业知识：我们专注于迪拜黄金签证的最新要求，确保您的申请合规且成功。',
    expert_guidance_description_3:
      '全面的房产投资指导：我们的专家帮助您找到符合黄金签证资格的最佳房产，同时最大化您的投资潜力。',
    expert_guidance_description_4:
      '完整文件支持：我们处理所有必要的文件工作，确保您的提交过程顺畅且准确。',
    expert_guidance_description_5:
      '专注的签证申请管理：我们管理您整个迪拜居留黄金签证流程，从初步咨询到最终批准。',
    expert_guidance_description_6:
      '批准后服务：我们的支持延伸至签证批准之后，提供在阿联酋的安置与融合协助。',
    expert_guidance_description_7:
      '家庭签证处理协助：我们为您的配偶、子女和父母也获取阿联酋家庭签证提供全程支持。',
    expert_guidance_description_8:
      '法律合规与咨询：我们的团队提供专业法律建议，确保您的黄金签证投资完全遵守阿联酋法律。',
    expert_guidance_description_9:
      '持续支持与咨询：在整个黄金签证旅程中，我们始终提供持续咨询与支持，让您安心无忧。',
    ready_to_secure: '准备好获取您的阿联酋黄金签证？',
    ready_to_secure_description:
      '迈出第一步获得阿联酋居留。我们的专家已准备好引导您完成整个黄金签证流程，并帮助您找到理想的房地产投资机会。',
    golden_visa_consultation: '黄金签证咨询',

    // 服务页 – 房产出售
    property_selling: '房产出售服务',
    sell_your_property: '出售您的房产',
    maximize_your_property: '最大化您在迪拜房产的曝光率',
    maximize_your_property_description:
      '我们的团队提供全面的房产出售服务，帮助您获得最佳投资回报。我们利用广泛的网络和强大的营销资源，让您的房产获得最大曝光，确保您的房产管理与出售过程顺利成功。',
    get_free_valuation: '获取免费房产估价',
    why_choose_x_space: '为什么选择 X-Space 出售房产？',
    why_choose_x_space_description:
      '凭借超过 9 年的迪拜房地产市场经验，我们提供无与伦比的房产出售服务，最大化您房产的价值并缩短上市时间。',
    maximize_exposure: '最大曝光',
    maximize_exposure_description:
      '我们利用庞大的网络和强大的营销渠道，为房产提供最佳展示，确保您的房屋触达大量合格买家。',
    expert_valuation: '专业估价',
    expert_valuation_description:
      '基于当前市场趋势和周边成交案例，提供精准可靠的房产估价，帮助您正确定价。',
    professional_marketing: '专业营销',
    professional_marketing_description:
      '我们的服务包括高质量摄影、沉浸式虚拟看房，以及优质房源展示，让您的房产脱颖而出。',
    dedicated_support: '专属支持',
    dedicated_support_description:
      '您将拥有一位专属经纪人，在整个房产出售过程中提供全程支持，从最初挂牌到最终成交。',
    our_proven_selling_process: '我们经过验证的销售流程',
    property_evaluation: '房产评估',
    property_evaluation_description:
      '我们从全面的市场分析和精准的房产估价开始，为您的挂牌制定最佳定价策略。',
    marketing_strategy: '营销策略',
    marketing_strategy_description:
      '我们的策略包括专业摄影、沉浸式虚拟看房，以及多渠道营销活动，让您的房产获得最大曝光。',
    buyer_screening: '买家筛选',
    buyer_screening_description:
      '我们负责所有潜在买家的预筛选，识别合格客户，确保交易过程顺利且认真。',
    negotiation: '谈判',
    negotiation_description:
      '我们经验丰富的经纪人将进行专业谈判，确保您获得最佳价格和有利条款。',
    documentation: '文件处理',
    documentation_description:
      '我们管理所有法律文件，确保完全遵守迪拜房地产法规，让交易无忧。',
    closing: '成交',
    closing_description:
      '我们的团队在最终过户过程中提供全程支持，确保您的房产顺利成功出售。',
    comprehensive_services: '全方位服务',
    whats_included: '我们的服务包含什么',
    whats_included_description:
      '我们的全套服务涵盖您在迪拜顺利出售房产所需的一切，以最短时间获得最佳价格。',
    whats_included_description_1:
      '免费专业房产估价：提供免费精准的估价，帮助您确定最佳挂牌价格。',
    whats_included_description_2:
      '高质量摄影与虚拟看房：我们提供高端视觉展示，包括高清照片和沉浸式虚拟看房。',
    whats_included_description_3:
      '多平台营销推广：通过多渠道推广活动，提高房产曝光度并吸引合格买家。',
    whats_included_description_4:
      '买家筛选与带看：我们处理所有买家咨询并筛选潜在客户，确保只考虑认真报价。',
    whats_included_description_5:
      '专业谈判与价格优化：我们的专家为您谈判，确保达成最佳价格与条款。',
    whats_included_description_6:
      '完整法律文件支持：我们处理所有法律文件，确保完全符合法律规定，交易顺利。',
    whats_included_description_7:
      '交易管理与成交支持：我们全程支持直至最终成交，确保顺利及时完成出售。',
    whats_included_description_8:
      '售后支持与协助：服务并不在成交时结束，我们继续为您的售后需求提供支持。',
    start_selling_process: '开始出售流程',
    ready_to_sell_your_property: '准备好出售您的房产了吗？',
    get_free_property_valuation: '获取免费房产估价',
    ready_to_sell_your_property_description:
      '从免费估价和咨询开始。我们的专家将帮助您最大化房产价值并确保顺利出售体验。',

    // 服务页 – 按揭咨询
    financing_solutions: '融资解决方案',
    mortgage_consultancy: '按揭咨询',
    find_the_right_financing: '为您的房产找到合适的融资方案',
    find_the_right_financing_description:
      '我们的按揭专家简化购房融资流程。我们提供个性化咨询，帮助您找到最佳贷款和融资选择，让您轻松在迪拜购置理想房产。',
    get_pre_approved: '获取预先审批',
    our_mortgage_services: '我们的按揭服务',
    our_mortgage_services_description:
      '根据您的财务需求和投资目标量身定制的全面按揭解决方案，适用于迪拜充满活力的房地产市场。',
    mortgage_pre_approval: '按揭预先审批',
    mortgage_pre_approval_description:
      '获得按揭预批，增强您的购房地位并简化购买流程。',

    investment_calculator: '投资计算器',
    investment_calculator_description:
      '使用我们的综合房地产投资计算器计算您的潜在回报。',
    dubai_real_estate_investments: '迪拜房地产投资计算器',
    dubai_real_estate_investments_description:
      '分析、计算并评估潜在的房地产回报',
    dubai_real_estate_investments_hold: '持有',
    dubai_real_estate_investments_flip: '快速出售',
    expected_capital_appreciation: '预期资本增值',
    expected_total_rental_income: '总租金收入',
    expected_aed: '迪拉姆',
    expected_usd: '美元',
    expected_investment_amount: '输入投资金额',
    expected_annual_appreciation: '预期年增值',
    expected_annual_rental_yield: '预期年租金收益率',
    expected_holding_period: '持有期（年）',
    expected_value_after: '期末价值',
    expected_value_years: '年',
    expected_value_exit_fee: '退出费用（2.5%）',
    expected_value_annual_cash_flow: '年度现金流',
    expected_value_entry_fee: '入场费（1.5%）',
    expected_reset: '重置',
    expected_investment_duration: '投资周期：',
    expected_expected_irr: '预期内部收益率(IRR)',
    expected_exit_multiple: '退出倍数',
    expected_project_duration: '项目周期（年）',
    expected_total_roi: '总投资回报',
    expected_flip_entry_fee: '入场费（2.5%）',
    expected_flip_exit_fee: '退出费用（2.5%）',

    rate_comparison: '利率比较',
    rate_comparison_description:
      '比较多家银行和金融机构的按揭利率，获取最佳方案。',
    loan_structuring: '贷款结构设计',
    loan_structuring_description:
      '提供贷款结构、条款和还款方式的专业建议，适合您的财务状况。',
    documentation_support: '文件支持',
    documentation_support_description:
      '为按揭文件、申请处理和提交提供全程协助。',
    fast_processing: '快速处理',
    fast_processing_description:
      '依托我们与银行的长期合作关系和简化的流程，实现按揭快速审批。',
    expert_guidance: '专家指导',
    expert_guidance_description:
      '由具备深厚市场知识的认证按揭专家提供个性化咨询。',
    mortgage_types_available: '可选按揭类型',
    conventional_mortgage: '传统按揭',
    conventional_mortgage_description:
      '为迪拜购房提供竞争性利率和灵活条款的传统按揭方案。',
    fixed_rate_mortgage: '固定利率按揭',
    fixed_rate_mortgage_description: '固定利率，月供稳定',
    variable_rate_mortgage: '浮动利率按揭',
    variable_rate_mortgage_description: '利率随市场变化灵活调整',
    up_to_80_financing: '最高可融资 80%',
    up_to_80_financing_description: '为符合条件的买家提供高贷款成数',
    islamic_mortgage: '伊斯兰按揭',
    islamic_mortgage_description: '符合伊斯兰教法和银行原则的融资方案',
    murabaha_financing: '穆拉巴哈融资',
    murabaha_financing_description: '符合伊斯兰教法的成本加成融资结构',
    ijara_financing: '伊加拉融资',
    ijara_financing_description: '租赁转购安排，逐步实现房产所有权转移',
    sharia_compliance: '伊斯兰合规',
    sharia_compliance_description: '完全遵循伊斯兰银行原则',
    our_mortgage_process: '我们的按揭流程',
    initial_consultation: '初步咨询',
    initial_consultation_description_mortgage:
      '提供免费咨询，评估您的财务状况并确定最佳按揭方案。',
    pre_approval: '预先审批',
    pre_approval_description: '通过预批，明确预算并增强购房报价的竞争力。',
    application_processing: '申请处理',
    application_processing_description:
      '在我们的协助下完成按揭申请并提交至首选贷款机构。',
    approval_closing: '审批与成交',
    approval_closing_description: '最终审批、房产估价与成交全程支持。',
    expert_mortgage_services: '专业按揭服务',
    expert_mortgage_services_description: '为什么选择我们的按揭咨询？',
    expert_mortgage_services_description_1:
      '凭借在迪拜按揭市场的丰富经验和与主要银行的紧密关系，我们提供全面且量身定制的按揭解决方案。',
    expert_mortgage_services_description_2: '可接入 15+ 家领先银行与贷款机构',
    expert_mortgage_services_description_3: '具备竞争力的利率与条款',
    expert_mortgage_services_description_4: '快速预批流程',
    expert_mortgage_services_description_5: '专业财务分析与建议',
    expert_mortgage_services_description_6: '完整文件支持',
    expert_mortgage_services_description_7: '个性化按揭方案',
    expert_mortgage_services_description_8: '批复后持续支持与指导',
    expert_mortgage_services_description_9: '无隐藏费用或收费',
    ready_to_secure_mortgage: '准备好获取按揭贷款了吗？',
    ready_to_secure_mortgage_description:
      '从免费咨询和预批开始。我们的专家将帮助您找到最佳融资方案，助您顺利完成购房。',
    mortgage_consultation: '按揭咨询',
  },
  fr: {
    // Navigation
    aboutUs: 'À propos de nous',
    properties: 'Propriétés',
    projects: 'Projets',
    buy: 'Acheter',
    rent: 'Louer',
    offPlanResale: 'Revente sur plan',
    offPlan: 'Projets sur plan',
    commercial: 'Commercial',
    properties: 'Propriétés',
    newsArticles: 'Actualités / Articles',
    newsArticles: 'Actualités / Articles',
    blog: 'Blog',
    agent: 'Agent',
    contactUs: 'Contactez-nous',

    // Hero Section
    heroTitle: 'Trouvez la propriété de vos rêves à Dubaï',
    heroSubtitle:
      'Découvrez des propriétés de luxe dans les endroits les plus prestigieux de Dubaï',

    // Property Search
    searchPlaceholder: 'Ville, communauté ou immeuble',
    propertyType: 'Type de propriété',
    bedsAndBaths: 'Chambres & Salles de bain',
    search: 'Rechercher',
    noResultsFound: 'Aucun résultat trouvé',
    tryDifferentSearch:
      'Essayez un autre terme de recherche ou explorez nos propriétés',
    clearSearch: 'Effacer la recherche',

    // Categories
    exploreServices: 'Découvrez nos services',
    buyProperties: 'Acheter des propriétés',
    buyDescription:
      'Trouvez votre maison idéale parmi notre sélection de propriétés à vendre.',
    rentProperties: 'Louer des propriétés',
    rentDescription:
      'Découvrez des locations haut de gamme dans les endroits les plus prisés de Dubaï.',
    offPlanProjects: 'Projets sur plan',
    offPlanDescription:
      "Investissez dans l'avenir de Dubaï avec nos développements exclusifs sur plan.",
    explore: 'Explorer',

    // Featured Properties
    featuredProperties: 'Propriétés en vedette',
    featuredDescription:
      'Découvrez notre sélection exclusive des meilleures propriétés de Dubaï',
    viewDetails: 'Voir les détails',
    viewAllProperties: 'Voir toutes les propriétés',

    // Agents Section
    meetOurAgents: 'Rencontrez Nos Agents Experts',
    realEstateAgents: 'Agents Immobiliers',
    agentsExperience:
      "Nos agents professionnels possèdent des années d'expérience sur le marché immobilier de Dubaï et sont prêts à vous aider à trouver la propriété de vos rêves. Grâce à leur connaissance approfondie du marché local et leur expertise dans différents segments immobiliers, ils vous guideront à chaque étape de votre parcours immobilier.",
    buySellInvest:
      "Que vous souhaitiez acheter, vendre ou investir sur le marché immobilier dynamique de Dubaï, nos agents s'engagent à vous offrir un service personnalisé adapté à vos besoins et préférences spécifiques.",
    yearsExperience: "Années d'Expérience",
    propertiesSold: 'Biens Vendus',
    contactAgent: "Contacter l'Agent",
    viewProfile: 'Voir le Profil',
    meetAllAgents: 'Voir Tous les Agents',
    showAllAgents: 'Voir Tout',
    specialization: 'Spécialisation',
    languages: 'Langues',
    contactNow: 'Contacter Maintenant',

    // Top Sales Section
    topSalesAgents: 'Meilleurs agents de vente',
    topSalesDescription: 'Reconnaissance de nos agents les plus performants',
    filterByPeriod: 'Filtrer par période',
    month: 'Mois',
    quarter: 'Trimestre',
    year: 'Année',
    salesVolume: 'Volume des ventes',
    transactions: 'Transactions',
    ranking: 'Classement',

    // Awards & Recognition
    awardsRecognition: 'Nos récompenses et reconnaissances',
    awardsDescription:
      'Nous sommes fiers d’être reconnus pour notre excellence en immobilier et notre satisfaction client.',
    scrollToExplore: 'Faites glisser pour explorer tous les prix',
    realEstateExcellence: 'Excellence en Immobilier',

    // Partner Developers
    partnerDevelopers: 'Nos partenaires promoteurs',
    partnerDevelopersDescription:
      'Nous travaillons avec les promoteurs les plus prestigieux de Dubaï pour vous offrir des propriétés et des opportunités d’investissement exceptionnelles.',
    visitWebsite: 'Visiter le site web',

    // Why Choose Us
    whyChooseUs: 'Pourquoi choisir X Space',
    whyChooseUsDescription:
      'Nous nous engageons à offrir un service exceptionnel et des solutions sur mesure pour tous vos besoins immobiliers à Dubaï.',
    marketExpertise: 'Expertise du marché',
    marketExpertiseDescription:
      'Notre équipe possède une connaissance approfondie du marché immobilier de Dubaï, vous aidant à prendre des décisions d’investissement éclairées.',
    trustedAdvisors: 'Conseillers de confiance',
    trustedAdvisorsDescription:
      'Avec des milliers de transactions réussies, nous avons bâti une réputation d’honnêteté, d’intégrité et de satisfaction client.',
    timeEfficiency: 'Efficacité temporelle',
    timeEfficiencyDescription:
      'Nous valorisons votre temps et optimisons le processus de recherche pour trouver rapidement votre propriété idéale.',
    awardWinningService: 'Service primé',
    awardWinningServiceDescription:
      'Reconnus pour notre excellence en services immobiliers, nous dépassons systématiquement les attentes de nos clients.',

    // News & Insights
    newsInsights: 'Actualités et analyses',
    newsInsightsDescription:
      'Restez informé des dernières nouvelles, tendances du marché et analyses du secteur immobilier à Dubaï.',
    latestNews: 'Dernières nouvelles',
    readMore: 'Lire la suite',
    featuredBlogPosts: 'Articles de blog en vedette',
    viewAll: 'Voir tout',
    browseAllArticles: 'Parcourir tous les articles',

    // FAQ
    faq: 'Foire aux questions',
    faqDescription:
      "Trouvez des réponses aux questions courantes sur l'achat, la vente et l’investissement immobilier à Dubaï.",
    cantFindAnswer: 'Vous ne trouvez pas la réponse que vous cherchez?',
    contactOurTeam: 'Contactez notre équipe',

    // Call to Action
    readyToFindProperty: 'Prêt à trouver la propriété de vos rêves?',
    readyToFindPropertyDescription:
      'Contactez notre équipe d’experts dès aujourd’hui et laissez-nous vous aider à trouver la propriété idéale à Dubaï correspondant à vos besoins et à votre budget.',
    contactUsNow: 'Contactez-nous maintenant',
    viewMoreProperties: 'Voir plus de propriétés',

    // Testimonials
    clientTestimonials: 'Ce que disent nos clients',
    clientTestimonialsDescription:
      'Lisez les témoignages de nos clients satisfaits à propos de leur expérience avec X Space Real Estate.',

    // Property Details
    bedrooms: 'Chambres',
    bathrooms: 'Salles de bain',
    reset: 'Réinitialiser',
    ok: 'OK',
    select: 'Sélectionner',
    beds: 'Lits',
    baths: 'Bains',
    commercial: 'Commercial',

    // Time periods
    january: 'Janvier',
    february: 'Février',
    march: 'Mars',
    april: 'Avril',
    may: 'Mai',
    june: 'Juin',
    july: 'Juillet',
    august: 'Août',
    september: 'Septembre',
    october: 'Octobre',
    november: 'Novembre',
    december: 'Décembre',
    q1: 'T1',
    q2: 'T2',
    q3: 'T3',
    q4: 'T4',

    // Footer
    footerDescription:
      "Votre partenaire de confiance dans l'immobilier à Dubaï, offrant des propriétés exceptionnelles et un service personnalisé.",
    company: 'Entreprise',
    properties: 'Propriétés',
    contactInfo: 'Infos de contact',
    allRightsReserved: 'Tous droits réservés.',
    privacyPolicy: 'Politique de confidentialité',
    termsOfService: "Conditions d'utilisation",
    cookiesPolicy: 'Politique de cookies',
    careers: 'Carrières',
    services: 'Services',
    newProjects: 'Nouveaux projets',
    workingHours: 'Lun - Sam, 9:30 - 18:30',
    location: 'Dubaï, EAU',

    atXSpace_1: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        Chez{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-Space Real Estate,
        </span>{' '}
        nous sommes une agence immobilière de confiance à Dubaï engagée à rendre
        l’immobilier accessible et rentable pour nos clients. Nous repoussons
        les limites de l’excellence immobilière, transformant le rêve de trouver
        votre maison idéale ou votre investissement en réalité.
      </p>
    ),
    atXSpace_2: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'>
        En tant que experts de l’immobilier à Dubaï, nous sommes là pour vous
        aider à sécuriser votre place dans l’avenir de l’immobilier.
      </p>
    ),
    learnMore: 'En savoir plus',
    yearsExperience: 'Années d’expérience',
    companyName: 'X - SPACE REAL ESTATE',
    welcometoXSpace: (
      <p className='text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-justify'>
        Bienvenue chez{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-Space Real Estate,
        </span>{' '}
        votre destination privilégiée pour l'achat, la vente et l'investissement
        dans de nouveaux horizons. Nous offrons des services immobiliers
        professionnels et nous spécialisons dans la mise en relation des clients
        avec des opportunités exceptionnelles sur le marché.
      </p>
    ),
    lookingToAcquire: (
      <p className=' text-lg text-gray-700 dark:text-gray-300 leading-relaxed text-justify'>
        Que vous souhaitiez{' '}
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          acheter, vendre ou louer un bien à Dubaï,
        </span>{' '}
        développer un nouveau projet ou investir dans un actif unique, notre
        équipe d’experts est là pour vous guider à chaque étape.
      </p>
    ),
    company_detail_1: (
      <p className='text-justify'>
        <span className='font-bold text-primarycolor dark:text-secondarycolor'>
          X-SPACE REAL ESTATE
        </span>{' '}
        anciennement connue sous le nom de Good Reputation Real Estate Broker,
        est une société de courtage immobilier et de gestion immobilière de
        premier plan basée à Dubaï. Fondée en 2017, elle s’est forgée une solide
        réputation en offrant un excellent service et des expériences clients
        exceptionnelles. Notre équipe dévouée d’ agents immobiliers multilingues
        à Dubaï s’engage à trouver la propriété parfaite qui répond à vos
        besoins.
      </p>
    ),
    company_detail_2: (
      <p className='text-justify'>
        Chaque année, nous gérons plus de 10 milliards d’AED de transactions
        immobilières. Nous sommes votre partenaire immobilier de confiance,
        construisant des relations solides en collaborant avec les meilleurs
        promoteurs des Émirats pour garantir à nos clients les meilleures
        propriétés et services. Notre approche de l’investissement immobilier
        intelligent repose sur une connaissance approfondie du marché, des
        recherches minutieuses et un engagement à fournir des conseils
        personnalisés.
      </p>
    ),
    company_detail_3: (
      <p className='text-justify'>
        Chez X-SPACE , nos valeurs fondamentales d’intégrité, d’excellence,
        d’innovation et de respect guident toutes nos actions. Nous opérons dans
        le strict respect des lois et réglementations des Émirats afin de
        garantir les plus hauts standards de professionnalisme et d’intégrité
        dans chaque transaction.
      </p>
    ),
    our_visionary_leaders: 'Nos Leaders Visionnaires',
    our_visionary_description: (
      <div>
        <p className='text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto'>
          Rencontrez les fondateurs qui révolutionnent l’industrie immobilière
          grâce à leur approche innovante et leur engagement indéfectible envers
          l’excellence.
        </p>
      </div>
    ),

    scroll_to_explore_agents: 'Balayez pour explorer tous les agents',

    our_values: 'Nos Valeurs',
    our_value_1: (
      <p className='text-gray-700 dark:text-gray-300 text-lg max-w-5xl mx-auto'>
        Nos valeurs fondamentales guident toutes nos actions, façonnant notre
        culture d’entreprise et influençant notre manière de servir nos clients
        sur le marché immobilier de Dubaï . Nous opérons avec une équipe
        immobilière diversifiée à Dubaï , garantissant ainsi un service de
        premier ordre.
      </p>
    ),
    integrity: 'Intégrité',
    integrity_description: (
      <div className='text-gray-700 dark:text-gray-300 text-center'>
        Nous bâtissons la confiance grâce à l’honnêteté et des pratiques
        commerciales transparentes.
      </div>
    ),
    excellence: 'Excellence',
    excellence_description: (
      <div className='text-gray-700 dark:text-gray-300 text-center'>
        Nous visons l’excellence des services immobiliers, en dépassant les
        attentes pour chaque bien que nous gérons.
      </div>
    ),
    innovation: 'Innovation',
    innovation_description: (
      <div className='text-gray-700 dark:text-gray-300 text-center'>
        Nous fournissons des solutions de pointe grâce à une réflexion créative
        et à des services immobiliers technologiques.
      </div>
    ),
    respect: 'Respect',
    respect_description: (
      <div className='text-gray-700 dark:text-gray-300 text-center'>
        Nous valorisons les perspectives diverses et traitons chacun avec le
        plus grand respect et dignité.
      </div>
    ),
    collaboration: 'Collaboration',
    collaboration_description: (
      <div className='text-gray-700 dark:text-gray-300 text-center'>
        Nous croyons au travail d’équipe pour obtenir des résultats
        exceptionnels, tant pour nos clients que pour notre équipe.
      </div>
    ),
    responsibility: 'Responsabilité',
    responsibility_description: (
      <div>
        Nous sommes engagés dans des pratiques éthiques et assumons la
        responsabilité de nos actions, démontrant ainsi notre profonde expertise
        du marché immobilier à Dubaï.
      </div>
    ),
    our_dynamic_team: 'Notre Équipe Dynamique',
    our_dynamic_description: (
      <div>
        <p className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
          Découvrez les professionnels dévoués qui font de X-Space Real Estate un
          leader de l’industrie.
        </p>
      </div>
    ),
    values: 'Valeurs',
    values_description_1: (
      <p>
        Au cœur de nos opérations, nous privilégions la communication claire, le
        dévouement et un service client exceptionnel. Grâce à une écoute
        attentive et une compréhension approfondie des besoins de nos clients,
        nous fournissons des conseils immobiliers complets et intégrés. Nous
        offrons des solutions immobilières innovantes et sommes fiers de notre
        engagement à fournir des résultats remarquables qui valorisent vos
        investissements immobiliers, à Dubaï et à l’international.
      </p>
    ),
    values_description_2: (
      <p>
        Dans chaque interaction, nous visons l’excellence de l’immobilier à
        Dubaï et construisons des relations durables basées sur la confiance et
        le respect mutuel. Cela garantit que chaque interaction est marquée par
        le professionnalisme et l’intégrité, vous offrant des conseils
        immobiliers personnalisés à chaque étape.
      </p>
    ),
    vision_and_mission: 'Vision & Mission',
    vision: 'Notre Vision',
    vision_description: (
      <p>
        Notre objectif est de devenir le principal prestataire de services
        immobiliers de la région et le premier choix des professionnels de
        l’immobilier. Nous nous engageons à construire des relations durables
        avec nos clients, fondées sur la transparence dans l’immobilier et la
        confiance mutuelle, tout en respectant les normes les plus élevées de
        professionnalisme.
      </p>
    ),
    mission: 'Notre Mission',
    mission_description: (
      <p>
        Nous réalisons notre vision en favorisant un environnement de travail
        dynamique, optimiste et performant, qui privilégie la création de
        partenariats durables. Notre succès se mesure par les résultats
        exceptionnels que nous offrons à nos clients. Nous combinons un état
        d’esprit ambitieux avec une attention personnalisée et un service client
        exceptionnel, vous guidant dans le processus d’achat et de vente de
        biens et vous aidant à obtenir un investissement immobilier rentable.
      </p>
    ),
    visit_our_office: 'Visitez Notre Bureau',
    visit_our_office_description: (
      <div className='text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto'>
        Idéalement situé à Dubaï, nous vous invitons à nous rendre visite pour
        discuter de vos besoins immobiliers en personne. Notre bureau est
        l’endroit où commence votre parcours immobilier à Dubaï.
      </div>
    ),
    ready_dream_property: (
      <h2 className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'>
        Prêt à Trouver Votre{' '}
        <span className='text-[#D4AF37]'>Propriété de Rêve</span> ?
      </h2>
    ),
    ready_dream_property_description: (
      <p className='text-lg text-gray-700 dark:text-gray-300 mb-8'>
        Laissez notre équipe d’experts vous guider. Que vous achetiez un bien à
        Dubaï, le vendiez ou investissiez, nous sommes là pour vous accompagner
        à chaque étape avec nos services immobiliers complets.
      </p>
    ),
    personalized_service: 'Service Personnalisé',
    personalized_service_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        Des solutions adaptées à vos besoins uniques
      </p>
    ),
    market_expertise: 'Expertise du Marché',
    market_expertise_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        Connaissance approfondie du marché immobilier de Dubaï
      </p>
    ),
    exclusive_listings: 'Annonces Exclusives',
    exclusive_listings_description: (
      <p className='text-gray-600 dark:text-gray-400'>
        Accès à des propriétés premium non disponibles ailleurs
      </p>
    ),
    contact_us_today: 'Contactez-nous dès aujourd’hui',

    // Services Page
    our_services: 'Nos Services',
    our_services_description:
      "Forte de plus de 9 ans d'expérience, X-Space Real Estate est une agence immobilière de confiance à Dubaï, offrant des solutions immobilières complètes aux acheteurs, vendeurs, propriétaires et investisseurs. Nous sommes spécialisés dans l'achat, la vente et la location de biens résidentiels et commerciaux dans les quartiers les plus prisés et les propriétés en pleine propriété de Dubaï. Nos services comprennent l'accompagnement avant et après-vente, le conseil en investissement immobilier sur plan, le conseil en financement hypothécaire, l'estimation immobilière et un marketing professionnel pour garantir une visibilité maximale et le meilleur prix. Nous proposons également une assistance pour l'obtention du Golden Visa et une analyse d'investissement détaillée afin d'aider nos clients à prendre des décisions immobilières éclairées et rentables. Notre équipe expérimentée s'engage à fournir un service personnalisé, une connaissance approfondie du marché et un accompagnement complet, vous permettant d'atteindre vos objectifs immobiliers en toute confiance sur le marché dynamique de Dubaï.",
    why_list_with_xspace: 'Pourquoi confier votre bien à X-Space Real Estate',
    why_list_with_xspace_description:
      'X-Space Real Estate est votre partenaire de confiance sur le marché immobilier de Dubaï. Nous sommes le choix privilégié des propriétaires et investisseurs, transformant les biens immobiliers en investissements rentables. Notre service fluide et notre connaissance approfondie du marché font de nous le partenaire idéal pour tous vos besoins immobiliers à Dubaï et dans les environs.',
    extensive_network: 'Réseau Étendu',
    extensive_network_description:
      'Nous exploitons notre vaste réseau d’agents professionnels et de partenaires marketing pour donner à votre bien une visibilité maximale.',
    legal_expertise: 'Expertise Juridique',
    legal_expertise_description:
      'Notre équipe fournit un accompagnement juridique complet, garantissant un processus fluide et sécurisé pour toutes les transactions immobilières à Dubaï.',
    media_resources: 'Ressources Médiatiques',
    media_resources_description:
      'Nous utilisons de nombreux canaux médiatiques pour maximiser la visibilité de votre bien et de nos services immobiliers.',
    real_estate_services: 'Services Immobiliers',
    service_buy: 'ACHETER',
    service_buy_title: 'Trouvez votre bien idéal à Dubaï',
    service_buy_description:
      'Que vous cherchiez à acheter une propriété pour votre maison de rêve ou un investissement rentable, nous vous guidons. Nos experts vous donnent accès à un large éventail de propriétés à vendre à Dubaï, y compris des biens de luxe, des villas et des appartements, afin que vous trouviez la solution idéale pour vos besoins.',

    service_sell: 'VENDRE',
    service_sell_title: 'Maximisez la visibilité de votre bien',
    service_sell_description:
      'Notre équipe fournit des services complets de vente afin d’obtenir le meilleur retour sur investissement. Nous utilisons notre vaste réseau et nos ressources marketing pour donner à votre propriété et vos annonces une visibilité maximale.',

    service_rent: 'LOUER',
    service_rent_title: 'Votre partenaire de confiance pour les locations',
    service_rent_description:
      'Nos experts immobiliers vous guideront dans le processus de recherche de votre location idéale. Nous proposons un accès à une large base de données de propriétés à louer à Dubaï, ainsi que des services tels que les locations de courte durée, le filtrage des locataires et l’inspection des biens.',

    residency_solutions: 'Solutions de Résidence',
    residency_solutions_title_1: 'Services Golden Visa',
    residency_solutions_title_2: 'Votre chemin vers la résidence aux Émirats',
    residency_solutions_description:
      'Nous accompagnons nos clients tout au long du processus d’obtention du Golden Visa de Dubaï. Notre équipe garantit une demande fluide en proposant les bons investissements immobiliers et le support documentaire, rendant la résidence à Dubaï plus facile et accessible. Nous gérons tous les services liés au visa en lien avec votre achat immobilier.',

    property_investment_guidance: 'Conseils en Investissement Immobilier',
    documentation_support: 'Support Documentaire',
    application_processing: 'Traitement des Demandes',
    legal_compliance: 'Conformité Juridique',

    financing_solutions: 'Solutions de Financement',
    financing_solutions_title_1: 'Conseil en Crédit Immobilier',
    financing_solutions_title_2: 'Trouvez le bon financement pour votre bien',
    financing_solutions_description:
      'Nos experts en prêts simplifient le processus de financement immobilier. Nous proposons des consultations personnalisées pour vous aider à trouver les meilleures options de prêt disponibles, rendant l’acquisition de votre propriété à Dubaï simple et rapide.',

    personalized_consultations: 'Consultations Personnalisées',
    best_loan_options: 'Meilleures Options de Prêt',
    application_support: 'Support de Candidature',

    ready_to_experience: (
      <h2 className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'>
        Prêt à découvrir{' '}
        <span className='text-[#D4AF37]'>
          {' '}
          des services immobiliers premium
        </span>{' '}
        ?
      </h2>
    ),

    ready_to_experience_description:
      'En tant que partenaire de confiance pour tous vos besoins immobiliers, nous proposons des services personnalisés et complets adaptés à vos besoins. Choisissez X-Space pour un accompagnement expert et des solutions immobilières fluides.',

    goldenVisa: 'Golden Visa',
    sellProperty: 'Vendre un Bien',
    mortgage: 'Crédit Immobilier',

    // Page des Services – Visa Doré
    uae_residency_solutions: 'Solutions de résidence aux Émirats',
    golden_visa_services: 'Services du Visa Doré',
    your_path: 'Votre chemin vers la résidence aux ÉAU',
    your_path_description:
      'Nous guidons les clients tout au long du processus d’obtention du Visa Doré de Dubaï. Notre équipe assure une application fluide en fournissant les bonnes options d’investissement immobilier aux ÉAU et un soutien documentaire, rendant la résidence à Dubaï plus facile et plus accessible.',
    start_application: 'Commencer votre demande',
    uae_golden_visa_benefits: 'Avantages du Visa Doré aux ÉAU',
    uae_golden_visa_benefits_description:
      'Le Visa Doré des ÉAU offre des bénéfices exceptionnels aux investisseurs, entrepreneurs et professionnels qualifiés souhaitant faire des ÉAU leur foyer à long terme.',
    ten_year_residency: 'Résidence de 10 ans',
    ten_year_residency_description:
      'Obtenez un visa de résidence à long terme renouvelable, valable dix ans, offrant stabilité et sécurité.',
    family_inclusion: 'Inclusion familiale',
    family_inclusion_description:
      'Étendez votre résidence aux ÉAU à votre conjoint(e), vos enfants et vos parents, permettant à votre famille de vivre avec vous dans le pays.',
    no_sponsor_required: 'Pas de sponsor requis',
    no_sponsor_required_description:
      'Profitez d’une indépendance totale. Le Visa Doré vous accorde la résidence sans sponsor aux ÉAU, ce qui vous offre une plus grande liberté.',
    multiple_entry: 'Entrée multiple',
    multiple_entry_description:
      'Voyagez sans restrictions avec des entrées et sorties illimitées des ÉAU, idéal pour les voyages d’affaires et personnels.',
    property_ownership: 'Propriété immobilière',
    property_ownership_description:
      'Obtenez la pleine propriété d’un bien dans les zones désignées en freehold, un avantage clé pour les investisseurs immobiliers à Dubaï et aux ÉAU.',
    business_opportunities: 'Opportunités d’affaires',
    business_opportunities_description:
      'Débloquez le potentiel de créer et de posséder une entreprise à 100 % dans de nombreux secteurs florissants aux ÉAU.',
    golden_visa_eligibility_crichtéria:
      'Critères d’éligibilité pour le Visa Doré',
    property_investment_route: 'Voie d’investissement immobilier',
    property_investment_route_description:
      'Découvrez les critères d’éligibilité pour obtenir le Visa Doré des ÉAU par un investissement stratégique ou des talents spécialisés.',
    min_investment: 'Investissement minimum : AED 2 millions',
    min_investment_description:
      'Qualifiez-vous pour le Visa Doré en achetant un bien à Dubaï ou dans d’autres émirats désignés d’une valeur minimale de 2 millions de dirhams des ÉAU.',
    freehold_properties: 'Propriétés en pleine propriété',
    freehold_properties_description:
      'L’investissement doit être dans des propriétés en pleine propriété (freehold) aux ÉAU, vous garantissant des droits de propriété complets dans les zones approuvées.',
    no_loan_restrictions: 'Aucune restriction de prêt',
    no_loan_restrictions_description:
      'Le bien peut être acquis avec un prêt hypothécaire, offrant des options flexibles pour votre investissement.',
    business_investment_route: 'Voie d’investissement commercial',
    business_investment_route_description:
      'Se qualifier par l’investissement commercial et les activités entrepreneuriales aux ÉAU.',
    business_investment_aed_10_million:
      'Investissement commercial : AED 10 millions',
    business_investment_aed_10_million_description:
      'Obtenez votre visa en créant ou en investissant dans des entreprises aux ÉAU avec un capital minimum de 10 millions de dirhams.',
    specialized_talents: 'Talents spécialisés',
    specialized_talents_description:
      'Le Visa Doré est disponible pour les professionnels très qualifiés, y compris les médecins, ingénieurs, scientifiques et artistes, qui peuvent démontrer des contributions exceptionnelles.',
    outstanding_students: 'Étudiants exceptionnels',
    outstanding_students_description:
      'Les étudiants et diplômés très performants sont également éligibles, reconnaissant l’excellence académique comme chemin vers la résidence à long terme.',
    our_golden_visa_process: 'Notre processus pour le Visa Doré',
    initial_consultation: 'Consultation initiale',
    initial_consultation_description:
      'Nous commençons par une consultation gratuite pour évaluer votre éligibilité et vous orienter vers la meilleure voie de demande de Visa Doré de Dubaï selon vos besoins spécifiques.',
    property_selection: 'Sélection de la propriété',
    property_selection_description:
      'Nos experts vous fournissent un accompagnement personnalisé pour choisir l’investissement immobilier idéal à Dubaï répondant entièrement aux exigences du Visa Doré.',
    property_documentation: 'Documentation',
    property_documentation_description:
      'Nous offrons une assistance complète pour la préparation de tous les documents nécessaires, leur attestation, et leur soumission aux autorités gouvernementales concernées. Nous veillons à ce que chaque détail soit traité correctement.',
    visa_approval: 'Approbation du visa',
    visa_approval_description:
      'Suivi de l’état de la demande et accompagnement jusqu’à l’approbation finale et l’émission du visa.',
    expert_guidance: 'Guide d’expertise',
    expert_guidance_description:
      'Pourquoi choisir X-Space pour votre Visa Doré ?',
    expert_guidance_description_1:
      'Avec plus de 9 ans d’expérience sur le marché immobilier de Dubaï et une connaissance approfondie des réglementations du Visa Doré aux ÉAU, nous offrons un soutien de bout en bout pour votre parcours résidentiel.',
    expert_guidance_description_2:
      'Connaissance experte des réglementations des visas aux ÉAU : nous nous spécialisons dans les dernières exigences du Visa Doré de Dubaï, en veillant à ce que votre demande soit conforme et réussie.',
    expert_guidance_description_3:
      'Orientation complète en investissement immobilier : nos experts vous aident à trouver les meilleurs biens immobiliers éligibles au Visa Doré, maximisant le potentiel de votre investissement.',
    expert_guidance_description_4:
      'Soutien complet de la documentation : nous prenons en charge toute la paperasse nécessaire, assurant une soumission fluide et précise.',
    expert_guidance_description_5:
      'Gestion dédiée de la demande de visa : nous gérons l’ensemble du processus de résidence dorée de la première consultation à l’approbation finale.',
    expert_guidance_description_6:
      'Services post-approbation : notre soutien se poursuit après l’approbation du visa, offrant une aide pour l’intégration et l’installation aux ÉAU.',
    expert_guidance_description_7:
      'Assistance pour les visas familiaux : nous offrons un soutien complet pour aider votre époux/épouse, vos enfants et vos parents à obtenir également le visa familial aux ÉAU.',
    expert_guidance_description_8:
      'Conformité légale et conseil : notre équipe fournit des conseils juridiques experts pour assurer que votre investissement pour le Visa Doré est entièrement conforme à la loi des ÉAU.',
    expert_guidance_description_9:
      'Soutien continu & consultation : nous sommes toujours disponibles pour des conseils et un soutien permanents pour votre parcours du Visa Doré, vous donnant une tranquillité d’esprit tout au long du processus.',
    ready_to_secure: 'Prêt à sécuriser votre Visa Doré émirati ?',
    ready_to_secure_description:
      'Faites le premier pas vers la résidence aux ÉAU. Nos experts sont prêts à vous guider tout au long du processus du Visa Doré et à vous aider à trouver l’investissement immobilier parfait.',
    golden_visa_consultation: 'Consultation Visa Doré',

    // Page des services – Vente de biens immobiliers
    property_selling: 'Services de vente immobilière',
    sell_your_property: 'Vendez votre bien',
    maximize_your_property: 'Maximisez la visibilité de votre bien à Dubaï',
    maximize_your_property_description:
      'Notre équipe offre des services complets de vente immobilière pour obtenir le meilleur retour sur votre investissement. Nous utilisons notre vaste réseau et nos puissantes ressources marketing pour donner à vos annonces immobilières une visibilité maximale, assurant un processus de gestion et de vente fluide et réussi.',
    get_free_valuation: 'Obtenez une estimation gratuite',
    why_choose_x_space: 'Pourquoi choisir X-Space pour vendre votre bien ?',
    why_choose_x_space_description:
      'Avec plus de 9 ans d’expertise sur le marché immobilier de Dubaï, nous proposons des services de vente inégalés qui maximisent la valeur de votre maison et réduisent son temps sur le marché.',
    maximize_exposure: 'Visibilité maximale',
    maximize_exposure_description:
      'Nous exploitons notre vaste réseau et nos canaux marketing puissants pour une visibilité optimale des annonces, garantissant que votre bien atteigne un large public d’acheteurs qualifiés.',
    expert_valuation: 'Évaluation experte',
    expert_valuation_description:
      'Obtenez des estimations précises et fiables basées sur les tendances actuelles du marché et les ventes comparables dans votre secteur, vous aidant à fixer le juste prix.',
    professional_marketing: 'Marketing professionnel',
    professional_marketing_description:
      'Nos services incluent des photographies de haute qualité, des visites virtuelles immersives et des présentations premium pour mettre votre bien en valeur.',
    dedicated_support: 'Accompagnement dédié',
    dedicated_support_description:
      'Un agent personnel vous accompagne tout au long du processus de vente, de l’inscription initiale à la conclusion finale.',
    our_proven_selling_process: 'Notre processus de vente éprouvé',
    property_evaluation: 'Évaluation du bien',
    property_evaluation_description:
      'Nous commençons par une analyse complète du marché immobilier et une évaluation précise afin de définir la stratégie de prix optimale.',
    marketing_strategy: 'Stratégie marketing',
    marketing_strategy_description:
      'Notre stratégie inclut des photographies professionnelles, des visites virtuelles et une campagne multicanale pour maximiser la visibilité de votre bien.',
    buyer_screening: 'Qualification des acheteurs',
    buyer_screening_description:
      'Nous gérons le pré-tri de tous les acheteurs potentiels, en identifiant les profils qualifiés pour assurer un processus d’achat sérieux et fluide.',
    negotiation: 'Négociation',
    negotiation_description:
      'Nos agents expérimentés assurent une négociation experte pour obtenir le meilleur prix et des conditions favorables.',
    documentation: 'Documentation',
    documentation_description:
      'Nous gérons toute la documentation légale, en assurant la conformité totale avec la réglementation immobilière de Dubaï pour une transaction sans souci.',
    closing: 'Finalisation',
    closing_description:
      'Notre équipe offre un accompagnement complet jusqu’au transfert final, garantissant une conclusion fluide et réussie de la vente de votre bien à Dubaï.',
    comprehensive_services: 'Services complets',
    whats_included: 'Ce qui est inclus dans notre service',
    whats_included_description:
      'Notre service complet comprend tout ce dont vous avez besoin pour vendre votre bien à Dubaï rapidement et au meilleur prix.',
    whats_included_description_1:
      'Évaluation immobilière gratuite et professionnelle : obtenez une estimation précise pour déterminer le prix optimal de votre bien.',
    whats_included_description_2:
      'Photographies de qualité & visites virtuelles : nous créons des visuels premium, dont des photos haute résolution et des visites immersives.',
    whats_included_description_3:
      'Campagne marketing multicanale : votre bien est promu via une campagne sur plusieurs plateformes pour atteindre une visibilité maximale.',
    whats_included_description_4:
      'Qualification & visites des acheteurs : nous gérons les demandes et filtrons les acheteurs afin de ne retenir que les offres sérieuses.',
    whats_included_description_5:
      'Négociation experte & optimisation du prix : notre équipe négocie pour vous afin d’obtenir le meilleur prix et les meilleures conditions.',
    whats_included_description_6:
      'Soutien complet à la documentation légale : nous gérons tous les documents et garantissons la conformité avec la législation immobilière de Dubaï.',
    whats_included_description_7:
      'Gestion des transactions & accompagnement jusqu’à la finalisation : nous offrons un soutien de bout en bout jusqu’à la vente finale.',
    whats_included_description_8:
      'Soutien post-vente : notre service ne s’arrête pas à la finalisation, nous proposons une assistance continue pour vos besoins après la vente.',
    start_selling_process: 'Commencez le processus de vente',
    ready_to_sell_your_property: 'Prêt à vendre votre bien ?',
    get_free_property_valuation: 'Obtenez une estimation gratuite',
    ready_to_sell_your_property_description:
      'Commencez par une évaluation gratuite et une consultation. Nos experts sont prêts à maximiser la valeur de votre bien et à garantir une vente fluide.',

    // Page des services – Conseil en prêt hypothécaire
    financing_solutions: 'Solutions de financement',
    mortgage_consultancy: 'Conseil hypothécaire',
    find_the_right_financing: 'Trouvez le financement adapté à votre bien',
    find_the_right_financing_description:
      'Nos experts hypothécaires simplifient le processus de financement immobilier. Nous offrons des consultations personnalisées pour vous aider à trouver le meilleur prêt et les meilleures options de financement afin de sécuriser facilement votre bien à Dubaï.',
    get_pre_approved: 'Obtenez une pré-approbation',
    our_mortgage_services: 'Nos services hypothécaires',
    our_mortgage_services_description:
      'Des solutions hypothécaires complètes adaptées à vos besoins financiers et à vos objectifs d’investissement immobilier dans le marché dynamique de Dubaï.',
    mortgage_pre_approval: 'Pré-approbation hypothécaire',
    mortgage_pre_approval_description:
      'Obtenez une pré-approbation pour renforcer votre position d’acheteur et simplifier le processus d’achat.',

    investment_calculator: "Calculateur d'investissement",
    investment_calculator_description:
      'Calculez vos rendements potentiels avec notre calculateur d’investissement immobilier complet.',
    dubai_real_estate_investments:
      "Calculateur d'investissements immobiliers à Dubaï",
    dubai_real_estate_investments_description:
      'Analysez, calculez et évaluez les rendements potentiels des biens immobiliers',
    dubai_real_estate_investments_hold: 'Conserver',
    dubai_real_estate_investments_flip: 'Revendre rapidement',
    expected_capital_appreciation: 'Appréciation du capital attendue',
    expected_total_rental_income: 'Revenu locatif total',
    expected_aed: 'AED',
    expected_usd: 'USD',
    expected_investment_amount: "Entrez le montant de l'investissement",
    expected_annual_appreciation: 'Appréciation annuelle attendue',
    expected_annual_rental_yield: 'Rendement locatif annuel attendu',
    expected_holding_period: 'Durée de détention (années)',
    expected_value_after: 'Valeur après',
    expected_value_years: 'Années',
    expected_value_exit_fee: 'Frais de sortie (2,5%)',
    expected_value_annual_cash_flow: 'Flux de trésorerie annuel',
    expected_value_entry_fee: 'Frais d’entrée (1,5%)',
    expected_reset: 'Réinitialiser',
    expected_investment_duration: "Durée de l'investissement :",
    expected_expected_irr: 'TRI attendu',
    expected_exit_multiple: 'Multiple de sortie',
    expected_project_duration: 'Durée du projet (années)',
    expected_total_roi: 'ROI total',
    expected_flip_entry_fee: 'Frais d’entrée (2,5%)',
    expected_flip_exit_fee: 'Frais de sortie (2,5%)',

    rate_comparison: 'Comparaison des taux',
    rate_comparison_description:
      'Comparez les taux hypothécaires de plusieurs banques et institutions financières pour obtenir la meilleure offre.',
    loan_structuring: 'Structuration de prêt',
    loan_structuring_description:
      'Conseils d’experts sur la structuration des prêts, les conditions et les options de remboursement adaptées à votre situation financière.',
    documentation_support: 'Support de documentation',
    documentation_support_description:
      'Assistance complète pour la documentation hypothécaire, le traitement et la soumission des demandes.',
    fast_processing: 'Traitement rapide',
    fast_processing_description:
      'Traitement accéléré grâce à nos relations bancaires établies et à des procédures simplifiées.',
    expert_guidance: 'Conseils d’experts',
    expert_guidance_description:
      'Consultation personnalisée avec des spécialistes certifiés en hypothèques, ayant une connaissance approfondie du marché.',
    mortgage_types_available: 'Types de prêts hypothécaires disponibles',
    conventional_mortgage: 'Prêts hypothécaires conventionnels',
    conventional_mortgage_description:
      'Options traditionnelles avec des taux compétitifs et des conditions flexibles pour l’achat de biens à Dubaï.',
    fixed_rate_mortgage: 'Prêts à taux fixe',
    fixed_rate_mortgage_description:
      'Paiements mensuels stables avec des taux d’intérêt fixes',
    variable_rate_mortgage: 'Prêts à taux variable',
    variable_rate_mortgage_description:
      'Taux flexibles qui s’ajustent selon les conditions du marché',
    up_to_80_financing: 'Jusqu’à 80% de financement',
    up_to_80_financing_description:
      'Rapports prêt/valeur élevés pour les acheteurs qualifiés',
    islamic_mortgage: 'Prêts hypothécaires islamiques',
    islamic_mortgage_description:
      'Solutions de financement conformes à la charia et aux principes bancaires islamiques.',
    murabaha_financing: 'Financement Mourabaha',
    murabaha_financing_description:
      'Structure de financement “coût majoré” conforme à la loi islamique',
    ijara_financing: 'Financement Ijara',
    ijara_financing_description:
      'Formule de location-vente avec transfert progressif de propriété',
    sharia_compliance: 'Conformité à la charia',
    sharia_compliance_description:
      'Entièrement conforme aux principes bancaires islamiques',
    our_mortgage_process: 'Notre processus hypothécaire',
    initial_consultation: 'Consultation initiale',
    initial_consultation_description_mortgage:
      'Consultation gratuite pour évaluer votre situation financière et déterminer les meilleures options hypothécaires.',
    pre_approval: 'Pré-approbation',
    pre_approval_description:
      'Obtenez une pré-approbation pour définir votre budget et renforcer votre offre.',
    application_processing: 'Traitement de la demande',
    application_processing_description:
      'Finalisation de la demande hypothécaire avec notre assistance et soumission aux prêteurs préférés.',
    approval_closing: 'Approbation & clôture',
    approval_closing_description:
      'Approbation finale, évaluation du bien et processus de clôture avec accompagnement complet.',
    expert_mortgage_services: 'Services hypothécaires experts',
    expert_mortgage_services_description:
      'Pourquoi choisir notre conseil hypothécaire ?',
    expert_mortgage_services_description_1:
      'Avec une vaste expérience sur le marché hypothécaire de Dubaï et de solides relations avec les principales banques, nous offrons des solutions complètes adaptées à vos besoins spécifiques.',
    expert_mortgage_services_description_2:
      'Accès à plus de 15 banques et prêteurs leaders',
    expert_mortgage_services_description_3:
      'Taux d’intérêt et conditions compétitifs',
    expert_mortgage_services_description_4:
      'Processus de pré-approbation rapide',
    expert_mortgage_services_description_5:
      'Analyse et conseils financiers d’experts',
    expert_mortgage_services_description_6: 'Support complet de documentation',
    expert_mortgage_services_description_7:
      'Solutions hypothécaires personnalisées',
    expert_mortgage_services_description_8:
      'Support et conseils après approbation',
    expert_mortgage_services_description_9: 'Aucun frais ou charge caché',
    ready_to_secure_mortgage: 'Prêt à obtenir votre prêt hypothécaire ?',
    ready_to_secure_mortgage_description:
      'Commencez avec une consultation gratuite et une pré-approbation. Nos experts sont prêts à vous aider à trouver la meilleure solution de financement pour l’achat de votre bien.',
    mortgage_consultation: 'Consultation hypothécaire',
  },
};

export function LanguageProvider({ children }) {
  // Get initial language from localStorage or default to English
  const [language, setLanguage] = useState('en');

  // Set document direction based on language
  useEffect(() => {
    const selectedLang = languages.find((lang) => lang.code === language);
    if (selectedLang) {
      document.documentElement.dir = selectedLang.dir;
      document.documentElement.lang = selectedLang.code;
    }

    // Store language preference
    localStorage.setItem('language', language);
  }, [language]);

  // Load saved language on initial render
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  // Get translation function
  const t = (key) => {
    const keys = key.split('.');
    let translation = translations[language];

    for (const k of keys) {
      if (translation && translation[k]) {
        translation = translation[k];
      } else {
        // Fallback to English if translation not found
        let fallback = translations['en'];
        for (const fk of keys) {
          if (fallback && fallback[fk]) {
            fallback = fallback[fk];
          } else {
            return key; // Return the key if no translation found
          }
        }
        return fallback;
      }
    }

    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Custom hook to use the language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
